<template>
  <div class="jcs-portal-card jcs-portal-reports">
    <div class="container">
      <div class="mi-wrap">
    <header class="mi-head">
      <h1 class="mi-title">🏭 Manufacturing Insights</h1>
      <p class="mi-sub">JC Steele &amp; Sons · Machine Shop · Shipping · Parts Sales</p>
      <div class="mi-fresh">
        <span class="badge" :class="mode === 'live' ? 'live' : 'demo'" :title="modeTitle">{{ mode === 'live' ? 'LIVE' : 'SNAPSHOT' }}</span>
        <span v-if="freshness" class="mi-pill" :class="'lvl-' + (freshness.level || 'ok')">
          <span class="mi-pill-dot" aria-hidden="true"></span>
          <span v-if="freshness.level === 'alert'">Some sources are overdue — see Data Sources.</span>
          <span v-else-if="freshness.level === 'lagging'">Catching up after the weekend — see Data Sources.</span>
          <span v-else-if="freshness.level === 'warning'">Awaiting some sources — see Data Sources.</span>
          <span v-else>Data is current.</span>
          <span v-if="freshness.data_through" class="mi-pill-meta">Updated through {{ fmtDate(freshness.data_through) }}<template v-if="freshness.next_refresh_label"> · Next update {{ freshness.next_refresh_label }}</template></span>
        </span>
      </div>
    </header>

    <div v-if="accessResolved && !canSee" class="mi-noaccess">
      <h2>Manufacturing Insights</h2>
      <p class="hint">You don't have access to this report. A Master can grant it in
        <b>Account Setup → Report Access</b>.</p>
    </div>

    <template v-else>
      <!-- Top KPI strip: current month + same-month-last-year + YTD + prior-YTD. Pinned above
           the tabs so it shows on every page, matching Foundry Insights. -->
      <div class="mi-kpis" v-if="ovCards.length">
        <div class="mi-kpi" v-for="c in ovCards" :key="c.key">
          <div class="mi-kpi-head">
            <span class="mi-kpi-label">{{ c.label }}</span>
            <button v-if="hasProv(c.key)" class="info-btn" type="button" @click.stop="showPop(c.key, $event)">ⓘ</button>
          </div>
          <div class="mi-kpi-value">{{ c.currentText }}</div>
          <div class="mi-kpi-when">{{ c.current_label }}</div>
          <div v-if="c.secondaryText" class="mi-kpi-secondary">{{ c.secondaryLabel }}: <b>{{ c.secondaryText }}</b></div>
          <div class="mi-kpi-cmp">
            <div class="mi-kpi-cmp-row">
              <span class="mi-kpi-cmp-lbl">{{ c.same_month_label }}</span>
              <span class="mi-kpi-cmp-val">{{ c.sameText }}</span>
              <span v-if="c.smDelta != null" class="mi-kpi-delta" :class="c.smCls" title="This month vs the same month one year ago"><span class="mi-kpi-delta-ar">{{ c.smArrow }}</span>{{ c.smPct }}</span>
            </div>
            <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">YTD</span><span class="mi-kpi-cmp-val">{{ c.ytdText }}</span></div>
            <div class="mi-kpi-cmp-row">
              <span class="mi-kpi-cmp-lbl">Yr ago YTD</span>
              <span class="mi-kpi-cmp-val">{{ c.priorText }}</span>
              <span v-if="c.ytdDelta != null" class="mi-kpi-delta" :class="c.ytdCls" title="Year-over-year YTD change"><span class="mi-kpi-delta-ar">{{ c.ytdArrow }}</span>{{ c.ytdPct }}</span>
            </div>
          </div>
        </div>
      </div>

      <nav class="mi-tabs">
        <a v-for="t in tabs" :key="t.id" href="javascript:void(0)"
           :class="{ active: activeTab === t.id }" @click="showTab(t.id)">{{ t.label }}</a>
      </nav>

      <!-- Chart range filter — narrows the monthly time-series charts on the current tab. KPI cards unaffected. -->
      <div class="mi-filters" v-if="['machineshop','shipping','parts','rmas','partcost'].indexOf(activeTab) !== -1">
        <span class="mi-filters-lbl">Chart range</span>
        <div class="mi-seg" role="group" aria-label="Chart range">
          <button type="button" :class="{ on: win === 6 }" @click="setWin(6)">6 mo</button>
          <button type="button" :class="{ on: win === 12 }" @click="setWin(12)">12 mo</button>
          <button type="button" :class="{ on: win === 999 }" @click="setWin(999)">All</button>
        </div>
      </div>

      <!-- shared drill-down card -->
      <div class="card wide drill" v-show="drill.open">
        <div class="drill-head">
          <div>
            <h2>{{ drill.title }}</h2>
            <p class="hint">{{ drill.hint }}</p>
          </div>
          <button type="button" class="drill-close" @click="drill.open = false">× close</button>
        </div>
        <div class="chartscroll">
          <div v-show="drill.kind === 'chart'" ref="drillMount"></div>
          <div v-show="drill.kind === 'table'" v-html="drill.html"></div>
        </div>
      </div>

      <!-- Machine Shop -->
      <section class="page" v-show="activeTab === 'machineshop'">
        <div class="grid">
          <div class="card wide">
            <h2>Machine Shop $/Machinist-Hour
              <button v-if="hasProv('machine_shop')" class="info-btn" type="button" @click.stop="showPop('machine_shop', $event)">ⓘ</button>
            </h2>
            <p class="hint">Receipts $ per machinist hour vs goal · green = at/above goal (higher is better) · <b>click a month for its weekly trend</b></p>
            <div class="chartscroll"><div ref="msMount"><div class="hint">Loading…</div></div></div>
          </div>
        </div>
      </section>

      <!-- Shipping -->
      <section class="page" v-show="activeTab === 'shipping'">
        <div class="grid">
          <div class="card wide">
            <div class="card-head-row">
              <h2>Ship-On-Time % — Original Promise vs Revised
                <button v-if="hasProv('shipping_otd')" class="info-btn" type="button" @click.stop="showPop('shipping_otd', $event)">ⓘ</button>
              </h2>
              <div class="mi-seg ship-basis" role="group" aria-label="On-time basis">
                <button type="button" :class="{ on: shipBasis === 'both' }" @click="setShipBasis('both')">Both</button>
                <button type="button" :class="{ on: shipBasis === 'promise' }" @click="setShipBasis('promise')">Original Promise</button>
                <button type="button" :class="{ on: shipBasis === 'revised' }" @click="setShipBasis('revised')">Revised</button>
              </div>
            </div>
            <p class="hint"><b>On-time to Original Promise</b> = shipped on/before the date first promised to the customer (the honest number). <b>On-time to Revised Date</b> = shipped on/before the current, possibly-changed date — what the system reports. The gap is orders shipped late but with the date pushed to match · dashed line = 95% goal · <b>click a month to see its orders</b></p>
            <div class="chartscroll"><div ref="shipMount"><div class="hint">Loading…</div></div></div>
          </div>
          <div class="card">
            <h2>Hidden Lateness<button v-if="hasProv('shipping_hidden_lateness')" class="info-btn" type="button" @click.stop="showPop('shipping_hidden_lateness', $event)">ⓘ</button></h2>
            <p class="hint">On-time on paper (vs the revised date) but late vs the original promise — the date was pushed out to match the ship date</p>
            <div class="ship-stat" v-if="shipGapLatest">
              <span class="ship-stat-val">{{ shipGapLatest.pct != null ? Number(shipGapLatest.pct).toFixed(1) + '%' : '—' }}</span>
              <span class="ship-stat-sub">{{ shipGapLatest.count != null ? shipGapLatest.count : 0 }} orders · {{ shipGapLatest.year }}-{{ String(shipGapLatest.month).padStart(2, '0') }}</span>
            </div>
            <div class="chartscroll"><div ref="shipGapMount"><div class="hint">Loading…</div></div></div>
          </div>
          <div class="card wide">
            <h2>Shipment Speed Mix<button v-if="hasProv('shipping_speed_mix')" class="info-btn" type="button" @click.stop="showPop('shipping_speed_mix', $event)">ⓘ</button></h2>
            <p class="hint">Order → ship time as a share of each month's shipments · <b>click a month to see its orders</b>
              <span class="ship-legend"><span class="sw" :style="{ background: legendColors.nextday }"></span>Next day</span>
              <span class="ship-legend"><span class="sw" :style="{ background: legendColors.twothree }"></span>2–3 d</span>
              <span class="ship-legend"><span class="sw" :style="{ background: legendColors.fourfourteen }"></span>4–14 d</span>
              <span class="ship-legend"><span class="sw" :style="{ background: legendColors.overfourteen }"></span>14+ d</span>
            </p>
            <div class="chartscroll"><div ref="shipSpeedMount"><div class="hint">Loading…</div></div></div>
          </div>
          <div class="card">
            <h2>Late Shipments by Department<button v-if="hasProv('shipping_late_by_dept')" class="info-btn" type="button" @click.stop="showPop('shipping_late_by_dept', $event)">ⓘ</button></h2>
            <p class="hint">Trailing 13 months · late orders attributed to the responsible area · <b>click a department for its trend</b></p>
            <div class="ship-dept" @click="onDeptClick" v-html="lateHtml"></div>
          </div>
        </div>

        <!-- Shipping drilldown: order-level detail (filterable) or a department trend -->
        <div class="card wide drill" v-show="shipDrill.open">
          <div class="drill-head">
            <div>
              <h2>{{ shipDrill.title }}</h2>
              <p class="hint">{{ shipDrill.hint }}</p>
            </div>
            <button type="button" class="drill-close" @click="shipDrill.open = false">× close</button>
          </div>
          <template v-if="shipDrill.kind === 'orders'">
            <div class="ship-filters">
              <input type="text" v-model="shipDrill.q" placeholder="Search order / customer…" />
              <select v-model="shipDrill.rep"><option value="">All reps</option><option v-for="r in shipDrillFacets.reps" :key="r" :value="r">{{ r }}</option></select>
              <select v-model="shipDrill.via"><option value="">All ship-via</option><option v-for="v in shipDrillFacets.vias" :key="v" :value="v">{{ v }}</option></select>
              <select v-model="shipDrill.flag"><option value="">All statuses</option><option v-for="f in shipDrillFacets.flags" :key="f" :value="f">{{ f }}</option></select>
              <select v-model="shipDrill.bucket"><option value="">All speeds</option><option v-for="b in shipDrillFacets.buckets" :key="b" :value="b">{{ b }}</option></select>
              <label class="ship-chk"><input type="checkbox" v-model="shipDrill.changedOnly" /> date-changed only</label>
              <span class="ship-count">{{ shipDrillFiltered.length }} of {{ shipDrill.rows.length }} orders</span>
            </div>
            <div class="chartscroll">
              <InsTable v-if="shipDrillFiltered.length" :columns="shipDrillCols" :rows="shipDrillFiltered" :glossary="fieldTips"
                        :filterable="false" export-name="mi-shipping-orders" row-noun="orders" tip-mode="click" dense
                        :row-class="(r) => r.OnTimeVsPromise === 'Late' ? 'late' : (r.IntegrityFlag && String(r.IntegrityFlag).indexOf('MATCH') !== -1 ? 'matched' : '')">
                <template #cell-OrderID="{ value }"><b>{{ value }}</b></template>
                <template #cell-OnTimeVsPromise="{ row }">{{ row.IntegrityFlag || row.OnTimeVsPromise }}</template>
              </InsTable>
              <div v-else class="hint">No orders match these filters.</div>
            </div>
          </template>
          <div v-else-if="shipDrill.kind === 'dept'" class="chartscroll"><div ref="shipDeptMount"></div></div>
        </div>
      </section>

      <!-- Parts Sales -->
      <section class="page" v-show="activeTab === 'parts'">
        <div class="grid">
          <div class="card wide">
            <h2>Parts Sales by Month
              <button v-if="hasProv('parts_sales')" class="info-btn" type="button" @click.stop="showPop('parts_sales', $event)">ⓘ</button>
            </h2>
            <p class="hint">JCSS invoiced parts revenue (VMFG) · <b>click a month for its invoices &amp; parts</b></p>
            <div class="chartscroll"><div ref="partsMount"><div class="hint">Loading…</div></div></div>
          </div>
        </div>

        <!-- Parts drilldown: invoice-level or part-line detail for a month (filterable + Excel export) -->
        <div class="card wide drill" v-show="partsDrill.open">
          <div class="drill-head">
            <div>
              <h2>{{ partsDrill.title }}</h2>
              <p class="hint">{{ partsDrill.mode === 'lines'
                ? 'Every invoiced part line this month · filter by customer / type / part family. Family is from the part master (Wear / Breakdown / Spare; “(unclassified)” for parts with no type — the majority of parts $).'
                : 'Every parts invoice this month · filter by customer / type. Switch to “Parts (lines)” for part-family / SKU detail.' }}</p>
            </div>
            <button type="button" class="drill-close" @click="partsDrill.open = false">× close</button>
          </div>
          <div class="parts-drill-toolbar">
            <div class="mi-seg" role="group" aria-label="Parts detail grain">
              <button type="button" :class="{ on: partsDrill.mode === 'invoices' }" @click="setPartsMode('invoices')">Invoices</button>
              <button type="button" :class="{ on: partsDrill.mode === 'lines' }" @click="setPartsMode('lines')">Parts (lines)</button>
            </div>
          </div>
          <div class="ship-filters">
            <input type="text" v-model="partsDrill.q" :placeholder="partsDrill.mode === 'lines' ? 'Search invoice / customer / part…' : 'Search invoice / customer…'" />
            <select v-model="partsDrill.cust"><option value="">All customers</option><option v-for="c in partsFacets.custs" :key="c" :value="c">{{ c }}</option></select>
            <select v-model="partsDrill.type"><option value="">All types</option><option v-for="t in partsFacets.types" :key="t" :value="t">{{ typeLabel(t) }}</option></select>
            <select v-if="partsDrill.mode === 'lines'" v-model="partsDrill.family"><option value="">All families</option><option v-for="f in partsFacets.families" :key="f" :value="f">{{ f }}</option></select>
            <button type="button" class="parts-export" @click="exportParts" :disabled="!partsFiltered.length">⬇ Excel</button>
            <span class="ship-count">{{ partsFiltered.length }} of {{ partsDrill.mode === 'lines' ? partsDrill.lines.length : partsDrill.inv.length }} · {{ usd(partsFilteredTotal) }}</span>
          </div>
          <div class="chartscroll">
            <div v-if="partsDrill.mode === 'lines' && !partsDrill.linesLoaded" class="hint">Loading…</div>
            <template v-else-if="partsFiltered.length">
              <InsTable v-if="partsDrill.mode === 'lines'" :columns="partsLineCols" :rows="partsFiltered" :glossary="fieldTips"
                        :filterable="false" row-noun="lines" tip-mode="click" dense :row-class="(r) => r.type === 'M' ? 'credit' : ''">
                <template #cell-part="{ row, value }"><b>{{ value }}</b><div v-if="row.partDesc" class="cust">{{ row.partDesc }}</div></template>
                <template #foot><tr><td colspan="7">{{ partsFiltered.length }} line(s)</td><td class="num">{{ usd(partsFilteredTotal) }}</td></tr></template>
              </InsTable>
              <InsTable v-else :columns="partsInvCols" :rows="partsFiltered" :glossary="fieldTips"
                        :filterable="false" row-noun="invoices" tip-mode="click" dense :row-class="(r) => r.type === 'M' ? 'credit' : ''">
                <template #cell-customer="{ row, value }">{{ value }}<em v-if="row.type === 'M'"> (credit memo)</em></template>
                <template #foot><tr><td colspan="4">{{ partsFiltered.length }} of {{ partsDrill.invCount }} invoice(s)</td><td class="num">{{ usd(partsFilteredTotal) }}</td></tr></template>
              </InsTable>
            </template>
            <div v-else class="hint">{{ (partsDrill.mode === 'lines' ? (partsDrill.lines||[]).length : (partsDrill.inv||[]).length) ? ('No ' + (partsDrill.mode === 'lines' ? 'part lines' : 'invoices') + ' match these filters.') : 'No detail available for this month (invoice/line detail covers the most recent ~13 months).' }}</div>
          </div>
        </div>
      </section>

      <!-- RMAs -->
      <section class="page" v-show="activeTab === 'rmas'">
        <div class="grid">
          <div class="card wide">
            <h2>RMAs by Month
              <button v-if="hasProv('rma')" class="info-btn" type="button" @click.stop="showPop('rma', $event)">ⓘ</button>
            </h2>
            <p class="hint">Return Material Authorizations created per month · trailing 13 months · <b>click a month for its returned parts</b></p>
            <div class="chartscroll"><div ref="rmaMount"><div class="hint">Loading…</div></div></div>
            <p v-if="rmaNote" class="hint">{{ rmaNote }}</p>
          </div>
          <div class="card wide">
            <h2>Why parts came back<button v-if="hasProv('rma_reasons')" class="info-btn" type="button" @click.stop="showPop('rma_reasons', $event)">ⓘ</button></h2>
            <p class="hint">Reason codes &amp; responsible department are <b>historical</b> — VISUAL reason entry lapsed after 2024.
              Current returns show the RMA type here, and the free-text reason per RMA in the month drill (Customer Service RMA LOG).
              Focus is part/manufacturing, not price.</p>
            <div class="chartscroll" v-html="rmaTopH"></div>
          </div>
        </div>

        <!-- RMA drilldown: returned-parts detail for a month (filterable) -->
        <div class="card wide drill" v-show="rmaDrill.open">
          <div class="drill-head">
            <div>
              <h2>{{ rmaDrill.title }}</h2>
              <p class="hint">{{ rmaDrill.hint }}</p>
            </div>
            <button type="button" class="drill-close" @click="rmaDrill.open = false">× close</button>
          </div>
          <div class="ship-filters">
            <input type="text" v-model="rmaDrill.q" placeholder="Search RMA / customer / part…" />
            <select v-model="rmaDrill.dept"><option value="">All depts</option><option v-for="d in rmaDrillFacets.depts" :key="d" :value="d">{{ d }}</option></select>
            <select v-model="rmaDrill.reason"><option value="">All reasons</option><option v-for="r in rmaDrillFacets.reasons" :key="r" :value="r">{{ r }}</option></select>
            <select v-model="rmaDrill.type"><option value="">All types</option><option v-for="t in rmaDrillFacets.types" :key="t" :value="t">{{ t }}</option></select>
            <select v-model="rmaDrill.status"><option value="">All statuses</option><option v-for="s in rmaDrillFacets.statuses" :key="s" :value="s">{{ s }}</option></select>
            <span class="ship-count">{{ rmaDrillFiltered.length }} of {{ rmaDrill.rows.length }} RMA lines</span>
          </div>
          <div class="chartscroll">
            <InsTable v-if="rmaDrillFiltered.length" :columns="rmaDrillCols" :rows="rmaDrillFiltered" :glossary="fieldTips"
                      :filterable="false" export-name="mi-rma-lines" row-noun="RMA lines" tip-mode="click" dense>
              <template #cell-part="{ row, value }"><b>{{ value }}</b><div v-if="row.partDesc" class="cust">{{ row.partDesc }}</div></template>
            </InsTable>
            <div v-else class="hint">No RMA lines match these filters.</div>
          </div>
        </div>
      </section>

      <!-- Forecasting -->
      <section class="page" v-show="activeTab === 'forecast'">
        <p class="hint">12-month projection (trend + seasonality) with a confidence band.
          Click the &#9432; for the method &amp; inputs behind each forecast.</p>
        <div class="grid">
          <div v-if="!forecastCards.length" class="hint">Loading…</div>
          <div v-for="(c, i) in forecastCards" :key="c.key" class="card wide">
            <h2>{{ c.label }} — 12-month forecast
              <button v-if="hasProv(c.key)" class="info-btn" type="button" @click.stop="showPop(c.key, $event)">ⓘ</button>
            </h2>
            <p class="hint">{{ c.method }} · confidence: <b>{{ c.confidence }}</b>
              <span v-if="c.goal != null"> · dashed line = goal</span></p>
            <div class="chartscroll"><div :ref="'fc' + i">
              <div class="hint" v-if="!c.forecast || !c.forecast.length">Not enough history to forecast yet.</div>
            </div></div>
          </div>
        </div>
      </section>

      <!-- Profitability (Machining/Assembly dept P&L) -->
      <section class="page" v-show="activeTab === 'profitability'">
        <div class="mi-seg" role="group" aria-label="Operation" style="margin-bottom:12px">
          <button type="button" :class="{ on: profitOp === 'MACHINING' }" @click="setProfitOp('MACHINING')">Machining</button>
          <button type="button" :class="{ on: profitOp === 'ASSEMBLY' }" @click="setProfitOp('ASSEMBLY')">Assembly</button>
        </div>
        <template v-if="profit.length && profitLatest">
          <div class="mi-kpis" style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:14px">
            <div class="mi-kpi">
              <div class="mi-kpi-label">Sales</div>
              <div class="mi-kpi-value">{{ pUsd(profitLatest.SalesMonthUsd) }}</div>
              <div class="mi-kpi-when">{{ profitLatest.MonthName }} {{ profitLatest.Year }}</div>
              <div class="mi-kpi-cmp">
                <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">Same mo. LY</span><span class="mi-kpi-cmp-val">{{ pUsd(profitLatest.SalesMonthPyUsd) }}</span></div>
                <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">YTD</span><span class="mi-kpi-cmp-val">{{ pUsd(profitLatest.SalesYtdUsd) }}</span></div>
                <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">Yr ago YTD</span><span class="mi-kpi-cmp-val">{{ pUsd(profitLatest.SalesYtdPyUsd) }}</span></div>
              </div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-label">Profit Contribution (YTD)</div>
              <div class="mi-kpi-value">{{ pUsd(profitLatest.ProfitYtdUsd) }}</div>
              <div class="mi-kpi-when">margin {{ pPct(profitLatest.ProfitMarginYtd) }} · YTD {{ profitLatest.Year }}</div>
              <div class="mi-kpi-cmp">
                <div class="mi-kpi-cmp-row">
                  <span class="mi-kpi-cmp-lbl">Yr ago YTD</span><span class="mi-kpi-cmp-val">{{ pUsd(profitLatest.ProfitYtdPyUsd) }}</span>
                  <span v-if="profitLatest.ProfitYtdVarPct != null" class="mi-kpi-delta" :class="pnum(profitLatest.ProfitYtdVarPct) >= 0 ? 'good' : 'bad'"><span class="mi-kpi-delta-ar">{{ pnum(profitLatest.ProfitYtdVarPct) >= 0 ? '▲' : '▼' }}</span>{{ pPct(pabs(profitLatest.ProfitYtdVarPct)) }}</span>
                </div>
                <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">This month</span><span class="mi-kpi-cmp-val">{{ pUsd(profitLatest.ProfitMonthUsd) }}</span></div>
              </div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-label">Sales / Labor-Hour</div>
              <div class="mi-kpi-value">{{ pUsd2(profitLatest.SalesPerHourMonth) }}</div>
              <div class="mi-kpi-when">{{ profitLatest.MonthName }} {{ profitLatest.Year }}</div>
              <div class="mi-kpi-cmp">
                <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">Same mo. LY</span><span class="mi-kpi-cmp-val">{{ pUsd2(profitLatest.SalesPerHourMonthPy) }}</span></div>
                <div class="mi-kpi-cmp-row"><span class="mi-kpi-cmp-lbl">YTD</span><span class="mi-kpi-cmp-val">{{ pUsd2(profitLatest.SalesPerHourYtd) }}</span></div>
              </div>
            </div>
          </div>
          <div class="grid">
            <div class="card wide" v-if="profitStmt && profitStmt.lines && profitStmt.lines.length">
              <h2>Income Statement — {{ profitOp === 'MACHINING' ? 'Machining' : 'Assembly' }} · {{ profitStmt.month }}/{{ profitStmt.year }}</h2>
              <p class="hint">Monthly departmental P&amp;L: sales, personnel + material, cost of goods sold, and profit contribution — with % of sales and YTD.</p>
              <div class="chartscroll">
                <table class="mi-wc-tbl">
                  <thead><tr><th>Line item</th><th class="num">Month $</th><th class="num">% sales</th><th class="num">YTD $</th><th class="num">YTD % sales</th><th class="num">YTD vs PY</th></tr></thead>
                  <tbody>
                    <tr v-for="(r,i) in profitStmt.lines" :key="'ps'+i" :class="{ 'wc-tot': r.section==='TOTAL' || r.section==='PROFIT' || r.section==='SALES', 'wc-sub': /^TOT_/.test(r.code||'') }">
                      <td>{{ r.label }}</td>
                      <td class="num">{{ r.isStat ? pnumfmt(r.monthAmt) : pUsd(r.monthAmt) }}</td>
                      <td class="num">{{ r.monthPct != null ? pPct(r.monthPct) : '' }}</td>
                      <td class="num">{{ r.isStat ? pnumfmt(r.ytdAmt) : pUsd(r.ytdAmt) }}</td>
                      <td class="num">{{ r.ytdPct != null ? pPct(r.ytdPct) : '' }}</td>
                      <td class="num" :class="r.ytdVarPct != null ? (pnum(r.ytdVarPct) >= 0 ? 'good' : 'bad') : ''">{{ r.ytdVarPct != null ? pPct(r.ytdVarPct) : '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
        <p v-else class="hint">Profitability data isn't available yet (awaiting the Accounting income-statement sync).</p>
      </section>

      <!-- Working Capital (inventory value / turns / dead stock, from live VMFG) -->
      <section class="page" v-show="activeTab === 'workingcapital'">
        <template v-if="wcHead">
          <div class="mi-kpis" style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:14px">
            <div class="mi-kpi">
              <div class="mi-kpi-label">Inventory — Accounting Basis</div>
              <div class="mi-kpi-value">{{ pUsd(wcFin(wcHead.FinancialBasisValue, wcHead.TotalInventoryValue)) }}</div>
              <div class="mi-kpi-when">{{ pInt(wcFin(wcHead.FinancialBasisPartCount, wcHead.PartCount)) }} parts · {{ wc.latest_date || '' }}</div>
            </div>
            <div class="mi-kpi" v-if="wcHead.FinancialBasisValue != null">
              <div class="mi-kpi-label">Excluded from Financial Inventory</div>
              <div class="mi-kpi-value">{{ pUsd(wcHead.ExcludedValue) }}</div>
              <div class="mi-kpi-when">{{ pInt(wcHead.ExcludedPartCount) }} parts · gross was {{ pUsd(wcHead.TotalInventoryValue) }}</div>
            </div>
            <div class="mi-kpi" v-if="wcHead.WipValue != null">
              <div class="mi-kpi-label">Work in Process</div>
              <div class="mi-kpi-value">{{ pUsd(wcHead.WipValue) }}</div>
              <div class="mi-kpi-when">{{ pInt(wcHead.WipWorkOrderCount) }} open work orders<template v-if="wcHead.WipBurdenValue != null"> · {{ pUsd(wcWipExBurden) }} excluding burden</template></div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-label">Dead Stock (no annual usage)</div>
              <div class="mi-kpi-value">{{ pUsd(wcHead.DeadStockValue) }}</div>
              <div class="mi-kpi-when">{{ pPct(pnum(wcHead.TotalInventoryValue) ? pnum(wcHead.DeadStockValue) / pnum(wcHead.TotalInventoryValue) : 0) }} of inventory · {{ pInt(wcHead.DeadStockPartCount) }} parts</div>
            </div>
            <div class="mi-kpi" v-if="wcHead.DeadStock10YrValue != null">
              <div class="mi-kpi-label">No Movement 10+ Years</div>
              <div class="mi-kpi-value">{{ pUsd(wcHead.DeadStock10YrValue) }}</div>
              <div class="mi-kpi-when">{{ pInt(wcHead.DeadStock10YrPartCount) }} parts · on the Accounting basis</div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-label">Inventory Turns</div>
              <div class="mi-kpi-value">{{ pTurns(wcHead.WeightedTurns) }}</div>
              <div class="mi-kpi-when">annual usage ÷ inventory value</div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-label">Slow Stock (&lt; 0.5 turns)</div>
              <div class="mi-kpi-value">{{ pUsd(wcHead.SlowStockValue) }}</div>
              <div class="mi-kpi-when">{{ pInt(wcHead.SlowStockPartCount) }} parts</div>
            </div>
          </div>
          <p class="hint">
            The headline is inventory on <strong>Accounting's inclusion criteria</strong> — parts marked Stocked in
            Part Maintenance, excluding IDs that begin "PUR", end "CR", or carry a finish code outside
            Accounting's list. It is a standard-cost extension of what Visual says is on hand, <strong>not a
            balance-sheet figure</strong>: no obsolescence reserve is netted, and on-hand quantities have not been
            physically counted. Foundry raw material is deliberately absent — it is valued from the monthly
            count workbook, not from Visual.
          </p>
          <div class="grid">
            <div class="card wide" v-if="wcBridge.length">
              <h2>Gross Visual Extension → Accounting Basis</h2>
              <p class="hint">How the raw Visual number reconciles to what Accounting counts as inventory. Each rule is
                applied once, in the order shown, so a part failing more than one is deducted a single time.</p>
              <div class="chartscroll">
                <table class="mi-wc-tbl">
                  <thead><tr><th>Step</th><th class="num">Parts</th><th class="num">Change</th><th class="num">Running total</th></tr></thead>
                  <tbody>
                    <tr v-for="(r,i) in wcBridge" :key="'wcb'+i" :class="{ 'wc-tot': r.stepKey === 'GROSS' || r.stepKey === 'NET' }">
                      <td>{{ r.label }}</td>
                      <td class="num">{{ pInt(r.parts) }}</td>
                      <td class="num" :class="pnum(r.delta) < 0 ? 'bad' : ''">{{ pnum(r.delta) ? pUsd(r.delta) : '' }}</td>
                      <td class="num">{{ pUsd(r.running) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="card wide" v-if="wcExcluded.length">
              <h2>Excluded from Financial Inventory</h2>
              <p class="hint">Every part the criteria removes, and which rule removed it. NOT_STOCKED is foundry raw
                material, tooling, and a long tail of low-value non-wear parts; CR_SUFFIX is mostly foundry sand cores;
                PUR_PREFIX is dominated by one placeholder part. Filter by reason to review a group.</p>
              <InsTable :columns="wcExclCols" :rows="wcExcluded" :glossary="fieldTips" sort-key="onHandValue" :sort-dir="-1" export-name="working-capital-excluded-parts" row-noun="parts" />
            </div>
            <div class="card wide" v-if="wcByCatProduct.length">
              <h2>Inventory Value by Product Code</h2>
              <p class="hint">Where the working capital sits — and how much of each category is dead stock. Highest value first.</p>
              <InsTable :columns="wcCatCols" :rows="wcByCatProduct" :glossary="fieldTips" sort-key="InventoryValue" :sort-dir="-1" export-name="working-capital-by-category" row-noun="categories">
                <template #cell-DeadPct="{ value }"><span :class="pnum(value) >= 0.5 ? 'bad' : ''">{{ pPct(value) }}</span></template>
              </InsTable>
            </div>
            <div class="card wide" v-if="wc && wc.slow_movers && wc.slow_movers.length">
              <h2>Top Slow / Dead Stock by Value</h2>
              <p class="hint">Parts tying up the most cash with little or no annual usage — candidates for review, rework, or write-down.</p>
              <InsTable :columns="wcSlowCols" :rows="wc.slow_movers" :glossary="fieldTips" sort-key="onHandValue" :sort-dir="-1" export-name="working-capital-slow-movers" row-noun="parts" />
            </div>
          </div>
        </template>
        <p v-else class="hint">Working Capital data isn't available yet (awaiting the VMFG inventory snapshot).</p>
      </section>

      <!-- ===== Part Cost Variance =====================================================
           Charts here are declarative SVG rather than MiSvgCharts mounts: the ±10% band is a
           drawn object with shape-encoded marks (circle inside / triangle outside), which is
           much clearer in the template and survives mono printing and colour blindness.
           Drills are MI-style inline cards, so all three levels can be on screen at once. -->
      <section class="page" v-show="activeTab === 'partcost'">
        <p class="hint" style="max-width:820px;margin:0 auto 14px">
          How each part's <b>cost per piece</b> has moved month to month. Every month is compared to the most
          recent <em>earlier</em> month that part had cost activity — not necessarily last month. A move of more
          than <b>±{{ pInt(pcvTol) }}%</b> is outside J.C. Steele's allowance<InsInfoTip term-key="pcv_rule" :glossary="fieldTips" mode="click" />
          and is called out below. Use the <b>Why flagged</b> column to work the list by the kind of problem
          rather than by size — a part quietly drifting past the allowance over six months never trips the
          monthly rule, so it will never appear at the top of a list sorted by dollars.
        </p>
        <p class="hint pcv-trust" style="max-width:820px;margin:0 auto 14px">
          <b>Read cost movement as a question, not a verdict.</b> These figures come from posted cost on
          inventory transactions, and VISUAL populates those cost fields for <b>closed</b> work orders only.
          A month's cost per piece therefore moves partly with how many orders happened to close in it, which
          is why a swing here is often a <em>costing</em> problem rather than a price problem — the postings
          drill exists to tell the two apart. Click any part, then any month, to see the individual postings.
        </p>

        <p v-if="pcvBusy" class="hint">Loading part cost variance…</p>
        <p v-else-if="!pcv" class="hint">Part cost variance isn't available yet (awaiting the nightly VMFG snapshot).</p>

        <template v-else>
          <!-- basis + watchlist toggles, using MI's existing mi-seg / mi-filters chrome -->
          <div class="mi-filters">
            <span class="mi-filters-lbl">Cost basis<InsInfoTip term-key="pcv_basis" :glossary="fieldTips" mode="click" /></span>
            <span class="mi-seg">
              <button type="button" :class="{ on: pcvBasis === 'sold' }" @click="pcvSetBasis('sold')">Sold to customers</button>
              <button type="button" :class="{ on: pcvBasis === 'made' }" @click="pcvSetBasis('made')">Made or bought</button>
            </span>
            <span class="mi-filters-lbl">Parts<InsInfoTip term-key="pcv_watchlist" :glossary="fieldTips" mode="click" /></span>
            <span class="mi-seg">
              <button type="button" :class="{ on: !pcvWatchOnly }" @click="pcvWatchOnly = false">All · {{ pInt(pcvParts.length) }}</button>
              <button type="button" :class="{ on: pcvWatchOnly }" @click="pcvWatchOnly = true">★ Watchlist · {{ pInt(pcvWatchCount) }}</button>
            </span>
            <!-- MI already carries an @media print block; it just had no way to reach it. Scoped to this
                 tab rather than the shared tab bar, so no other MI tab changes. -->
            <span class="mi-seg"><button type="button" @click="pcvPrint()" title="Printer-friendly view of this tab">🖨 Print</button></span>
          </div>
          <p v-if="pcvBasis === 'made'" class="hint" style="max-width:820px;margin:0 auto 12px">
            On the <b>made or bought</b> basis, cost per piece normally falls as lot size rises — bigger runs spread
            setup over more pieces and bigger purchases get price breaks. That is why far more parts land outside the
            allowance here than on the sold basis. Rows whose lot size shifted materially are marked
            <span class="pcv-lot">lot</span>, and the move is partly lot size, not price.
          </p>
          <p v-if="pcvWatchOnly" class="hint" style="max-width:820px;margin:0 auto 12px">
            Showing watchlist parts only — the cards below still count every part on this basis.
          </p>

          <!-- KPI strip, using MI's own .mi-kpi markup (MI has no insights-theme.css, so the
               shared InsKpiCard would render unstyled here). -->
          <div class="mi-kpis">
            <div class="mi-kpi pcv-kpi-click" @click="pcvOpenParts('breach')">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Outside ±{{ pInt(pcvTol) }}%</span>
                <InsInfoTip term-key="pcv_breach" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value" :class="{ 'pcv-over': pnum(pcv.breach_count) > 0 }">{{ pInt(pcv.breach_count) }}</div>
              <div class="mi-kpi-when">of {{ pInt(pcv.basis_count) }} comparable · {{ pcvLatestLabel }}</div>
              <div class="mi-kpi-secondary">
                <span class="pcv-over">{{ pInt(pcv.over_count) }} up</span> /
                <span class="pcv-under">{{ pInt(pcv.under_count) }} down</span>
              </div>
            </div>
            <!-- The blind spot of the ±10% rule, so it sits next to the breach count rather than
                 buried in a chart: these parts never trip the monthly test but are still drifting. -->
            <div class="mi-kpi">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Quietly Drifting</span>
                <InsInfoTip term-key="pcv_drifting" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value" :class="{ 'pcv-over': pnum(pcv.drifting_count) > 0 }">{{ pInt(pcv.drifting_count) }}</div>
              <div class="mi-kpi-when">past ±{{ pInt(pcvTol) }}% over 6 months, no single month tripped it</div>
              <div class="mi-kpi-secondary">
                <span class="pcv-over">{{ pInt(pcv.drifting_up) }} getting dearer</span>
                · {{ pInt(pcv.sustained_runs) }} on a 3+ month run
              </div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Cost Impact</span>
                <InsInfoTip term-key="pcv_impact" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value" :class="{ 'pcv-over': pnum(pcv.impact_usd) > 0 }">{{ pUsd(pcv.impact_usd) }}</div>
              <div class="mi-kpi-when">at this month's quantities</div>
            </div>
            <div class="mi-kpi pcv-kpi-click" @click="pcvOpenParts('all')">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Comparable Parts</span>
                <InsInfoTip term-key="pcv_comparable" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value">{{ pInt(pcv.basis_count) }}</div>
              <div class="mi-kpi-when">priced in 2+ months · {{ pInt(pcv.parts_seen) }} active</div>
            </div>
            <div class="mi-kpi">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Median Change</span>
                <InsInfoTip term-key="pcv_median" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value">{{ pcvSignedPct(pcv.median_change_pct) }}</div>
              <div class="mi-kpi-when">month over month</div>
            </div>
            <div v-if="pcv.top_part" class="mi-kpi pcv-kpi-click" @click="pcvOpenPart(pcv.top_part)">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Biggest Recent Increase</span>
                <InsInfoTip term-key="pcv_top" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value pcv-over">{{ pcvSignedPct(pcv.top_change_pct) }}</div>
              <div class="mi-kpi-when">{{ pcv.top_part }} · {{ pUsd(pcv.top_impact_usd) }}</div>
            </div>
            <div v-if="pnum(pcv.nobasis_count) > 0" class="mi-kpi pcv-kpi-click pcv-kpi-review" @click="pcvOpenParts('nobasis')">
              <div class="mi-kpi-head">
                <span class="mi-kpi-label">Not Comparable</span>
                <InsInfoTip term-key="pcv_nobasis" :glossary="fieldTips" mode="click" />
              </div>
              <div class="mi-kpi-value">{{ pInt(pcv.nobasis_count) }}</div>
              <div class="mi-kpi-when">first month ever costed</div>
            </div>
          </div>

          <div v-if="pnum(pcv.breach_count) > 0" class="pcv-banner" @click="pcvOpenParts('breach')">
            <b>{{ pInt(pcv.breach_count) }} parts moved more than ±{{ pInt(pcvTol) }}%</b> in {{ pcvLatestLabel }} —
            {{ pUsd(pcv.impact_usd) }} of cost change at this month's quantities. Click to list them.
            <em v-if="pnum(pcv.longgap_count) > 0">{{ pInt(pcv.longgap_count) }} of them compare back more than
              {{ pInt(pcv.max_gap_months || 3) }} months<span v-if="pnum(pcv.overyear_count) > 0"> ({{ pInt(pcv.overyear_count) }} more than a year)</span>
              — that's slow drift rather than a sudden jump, so sort on <b>Gap</b> in the table to separate them.</em>
            <em v-if="pnum(pcv.nobasis_count) > 0">{{ pInt(pcv.nobasis_count) }} more couldn't be compared at all and are <b>not</b> counted here.</em>
          </div>

          <div class="grid">
            <!-- Both charts below are v-if'd on their data, so a failed request used to render as
                 nothing at all. Say so instead. -->
            <div class="card wide" v-if="pcvChartError && !pcvLines.length">
              <h2>Cost per Piece Trend</h2>
              <p class="hint">
                <b>This chart could not be loaded.</b> {{ pcvChartError }}
                The KPI cards, Biggest Movers and the table below are unaffected — they come from
                different queries and the figures they show are correct.
              </p>
            </div>

            <!-- Chart A — cost trend lines against the ±10% allowance band -->
            <div class="card wide" v-if="pcvLines.length">
              <h2>Cost per Piece Trend<InsInfoTip term-key="pcv_trendchart" :glossary="fieldTips" mode="click" /></h2>
              <p class="hint">
                One line per part. Each part starts at 100 in its first month here, so the line reads as
                <em>“how far this part's cost has moved since then”</em> — that puts a $0.42 washer and a $25,000 auger on
                the same axis. The shaded band is the ±{{ pInt(pcvTol) }}% allowance.
                <b>A line that walks out of the band and stays out is a trend, not a blip</b> — that is the shape to act on.
                Note this band measures the move <em>since the start of the window</em>; the {{ pInt(pcvTol) }}% figure in
                the table below is month-to-month, so a part can sit outside here while every single month looks fine.
              </p>
              <p class="hint" v-if="pcvLinesQualified > pcvLines.length">
                Showing {{ pInt(pcvLines.length) }} of {{ pInt(pcvLinesQualified) }} parts that {{ pcvSelectionRule }}.
                Past about a dozen lines the chart stops being readable. The table below has every part.
              </p>
              <div class="chartscroll">
                <svg :viewBox="`0 0 ${pcvW} ${pcvLineH}`" preserveAspectRatio="none" style="height:260px">
                  <!-- allowance band, drawn first so the lines sit on top of it -->
                  <rect class="pcv-band" :x="pcvPad" :y="pcvIdxY(100 + pcvTol)" :width="pcvW - pcvPad * 2"
                        :height="Math.max(1, pcvIdxY(100 - pcvTol) - pcvIdxY(100 + pcvTol))" />
                  <line class="pcv-band-edge" :x1="pcvPad" :y1="pcvIdxY(100 + pcvTol)" :x2="pcvW - pcvPad" :y2="pcvIdxY(100 + pcvTol)" />
                  <line class="pcv-band-edge" :x1="pcvPad" :y1="pcvIdxY(100 - pcvTol)" :x2="pcvW - pcvPad" :y2="pcvIdxY(100 - pcvTol)" />
                  <line class="grid-line" :x1="pcvPad" :y1="pcvIdxY(100)" :x2="pcvW - pcvPad" :y2="pcvIdxY(100)" />
                  <text :x="pcvPad + 4" :y="pcvIdxY(100 + pcvTol) - 3" class="pcv-band-lbl">+{{ pInt(pcvTol) }}% allowance</text>
                  <text :x="pcvPad + 4" :y="pcvIdxY(100 - pcvTol) + 10" class="pcv-band-lbl">−{{ pInt(pcvTol) }}% allowance</text>
                  <text :x="pcvW - pcvPad - 2" :y="pcvIdxY(100) - 3" text-anchor="end" class="pcv-band-lbl">where each part started</text>

                  <g v-for="(ln, li) in pcvLines" :key="'pl' + li" class="pcv-line-g"
                     :class="{ 'pcv-line-mute': pcvHoverPart && pcvHoverPart !== ln.PartId }"
                     @click="pcvOpenPart(ln.PartId)"
                     @mouseenter="pcvHoverPart = ln.PartId" @mouseleave="pcvHoverPart = null">
                    <polyline class="pcv-line" :class="ln.cls" :stroke="ln.color" :points="ln.points" />
                    <circle v-for="(p, pi) in ln.pts" :key="'pp' + pi" class="pcv-lpt" :class="{ 'pcv-lpt-clamp': p.clamped }"
                            :cx="p.x" :cy="p.y" :r="p.clamped ? 3.2 : 2.4" :fill="ln.color">
                      <title>{{ pcvPtTitle(ln, p) }}</title>
                    </circle>
                    <text v-if="ln.end" :x="ln.end.x + 4" :y="ln.end.y + 3" class="pcv-line-lbl" :fill="ln.color">{{ ln.short }}</text>
                  </g>
                  <!-- x axis: the months the window covers. The band labels already carry the y scale. -->
                  <template v-for="(m, i) in pcvView">
                    <text v-if="pcvMoTick(i, pcvView.length)" :key="'ax' + i" :x="pcvX(i)" :y="pcvH - 4"
                          text-anchor="middle" class="pcv-axlbl">{{ pcvMoShort(m) }}</text>
                  </template>
                </svg>
              </div>
              <div class="ship-legend" v-for="(ln, li) in pcvLines" :key="'plg' + li"
                   :class="{ 'pcv-leg-mute': pcvHoverPart && pcvHoverPart !== ln.PartId }"
                   @mouseenter="pcvHoverPart = ln.PartId" @mouseleave="pcvHoverPart = null">
                <span class="sw" :style="{ background: ln.color }"></span>{{ ln.watched ? "★ " : "" }}{{ ln.PartId }}
                <b :class="ln.endIdx >= 100 + pcvTol ? 'pcv-up-t' : ln.endIdx <= 100 - pcvTol ? 'pcv-dn-t' : ''">{{ pcvSignedPct(ln.endIdx - 100) }}</b>
              </div>
              <p class="hint">
                Hover a line to isolate it; click for that part's full history and the postings behind it.
                <span v-if="pcvAnyClamped">A larger dot means the point is past the top or bottom of the axis — hover it for the real number.</span>
              </p>
            </div>

            <!-- Chart B — breach counts by month -->
            <div class="card wide" v-if="pcvView.length">
              <h2>Parts Outside the ±{{ pInt(pcvTol) }}% Allowance by Month<InsInfoTip term-key="pcv_breach" :glossary="fieldTips" mode="click" /></h2>
              <p class="hint">Counted only among parts that could be compared that month. Lower is better. A month where a
                part had no activity is skipped, not counted as an unchanged month. Click a bar for that month's list.</p>
              <div class="chartscroll">
                <svg :viewBox="`0 0 ${pcvW} ${pcvH}`" preserveAspectRatio="none" style="height:200px">
                  <g v-for="(m, i) in pcvView" :key="'pb' + i">
                    <rect class="pcv-mark" :x="pcvGrpX(i, 0)" :width="pcvGrpW" :y="pcvBarY(m.OverCount)"
                          :height="pcvBarH(m.OverCount)" fill="#b3402f" @click="pcvOpenParts('month', m)"><title>{{ pcvMoShort(m) }} {{ m.Year }} — {{ pInt(m.OverCount) }} part(s) up more than {{ pInt(pcvTol) }}%, of {{ pInt(m.ComparableCount) }} comparable (click)</title></rect>
                    <rect class="pcv-mark" :x="pcvGrpX(i, 1)" :width="pcvGrpW" :y="pcvBarY(m.UnderCount)"
                          :height="pcvBarH(m.UnderCount)" fill="#2f7a4f" @click="pcvOpenParts('month', m)"><title>{{ pcvMoShort(m) }} {{ m.Year }} — {{ pInt(m.UnderCount) }} part(s) down more than {{ pInt(pcvTol) }}% (click)</title></rect>
                    <text :x="pcvX(i)" :y="pcvBarTop(m)" text-anchor="middle" class="barlabel">{{ pPct(pcvBreachPct(m) / 100) }}</text>
                  </g>
                  <!-- Axes. Month names on x and the top of the count scale, so the chart reads without hover. -->
                  <template v-for="(m, i) in pcvView">
                    <text v-if="pcvMoTick(i, pcvView.length)" :key="'bx' + i" :x="pcvX(i)" :y="pcvH - 4"
                          text-anchor="middle" class="pcv-axlbl">{{ pcvMoShort(m) }}</text>
                  </template>
                  <text :x="pcvPad" :y="pcvPad" class="pcv-axlbl">{{ pInt(pcvBreachMax) }} parts</text>
                </svg>
              </div>
              <p class="hint">Label above each month = share of comparable parts outside the band.</p>
            </div>

            <!-- Chart C — biggest movers -->
            <div class="card wide" v-if="pcvMovers.length">
              <h2>Biggest Movers — {{ pcvLatestLabel }}<InsInfoTip term-key="pcv_impactcol" :glossary="fieldTips" mode="click" /></h2>
              <p class="hint">Ranked by dollars so a big percent on a cheap part doesn't crowd out real money. The pale strip
                in the middle of each bar is the ±{{ pInt(pcvTol) }}% allowance — a bar that stops inside it is fine.
                Click a row for the part's full cost history.</p>
              <div v-for="(r, i) in pcvMovers" :key="'pm' + i" class="pcv-mrow" @click="pcvOpenPart(r.PartId)">
                <div class="pcv-mpart" :title="r.PartDescription || r.PartId">{{ r.PartId }}<span v-if="pcvWatched(r)" class="pcv-star">★</span></div>
                <div class="pcv-meter">
                  <div class="pcv-tol" :style="pcvTolStyle"></div>
                  <div class="pcv-mid"></div>
                  <div class="pcv-mbar" :class="pnum(r.ChangePct) >= 0 ? 'up' : 'down'" :style="pcvMoverStyle(r)"></div>
                </div>
                <div class="pcv-mval">
                  <span :class="pcvCls(r)">{{ pcvSignedPct(r.ChangePct) }}</span>
                  <span v-if="pcvLotShift(r)" class="pcv-lot" :title="pcvCaveat(r)">lot</span>
                  <span class="pcv-mdim"> · {{ pcvUsd4(r.PriorUnitCost) }} → {{ pcvUsd4(r.LatestUnitCost) }} · {{ pUsd(r.ImpactUsd) }}</span>
                </div>
              </div>
            </div>

            <!-- Table 1 — every comparable part -->
            <div class="card wide">
              <h2>Part Cost Variance</h2>
              <p class="hint">{{ pInt(pcvRows.length) }} comparable parts. Each row is that part's own most recent month with
                cost activity, which for a part that hasn't moved in a while may be months or years ago — the
                <b>Months behind</b> column says so. Ordered as a review queue: <b>Why flagged</b> first, biggest
                dollars within each kind. Sort by <b>Impact $</b> for pure money, by <b>Change</b> for the biggest
                percentage swings, and use Filters to set a minimum impact so pennies-per-piece parts drop out.
                <span v-if="pcvPartsTruncated > 0"><b>{{ pInt(pcvPartsTruncated) }} lower-impact parts aren't listed</b> — the list is capped at the largest movers,
                so the queue re-orders the biggest movers rather than covering every part.</span>
              </p>
              <InsTable :columns="pcvCols" :rows="pcvRows" :glossary="fieldTips" tip-mode="click"
                        sort-key="ReviewRank" :sort-dir="1" column-chooser
                        export-name="mi-part-cost-variance" row-noun="parts" :page-size="5" dense
                        empty-text="No comparable parts on this basis."
                        :row-class="() => 'clickrow'" @row-click="(r) => pcvOpenPart(r.PartId)">
                <template #cell-PartId="{ row, value }">{{ value }}<span v-if="pcvWatched(row)" class="pcv-star">★</span><div v-if="row.PartDescription" class="cust">{{ row.PartDescription }}</div></template>
                <template #cell-ChangePct="{ row, value }"><span :class="pcvCls(row)">{{ pcvSignedPct(value) }}</span><span v-if="pcvLotShift(row)" class="pcv-lot" :title="pcvCaveat(row)">lot</span></template>
                <template #cell-ChangeUsd="{ row, value }"><span :class="pcvCls(row)">{{ (pnum(value) > 0 ? '+' : '') + pcvUsd4(value) }}</span></template>
                <template #cell-MonthsSincePrior="{ value }"><span :class="pnum(value) > 3 ? 'pcv-nobasis' : ''">{{ pInt(value) }}</span></template>
                <template #cell-MonthsStale="{ value }"><span :class="pnum(value) >= 12 ? 'pcv-nobasis' : ''">{{ pnum(value) === 0 ? '—' : pInt(value) }}</span></template>
                <template #cell-Band="{ row, value }"><span class="pcv-chip" :class="pcvChipCls(row)">{{ value }}</span></template>
                <template #cell-ReviewFlag="{ row }"><span v-if="!row.ReviewFlags.length" class="pcv-mdim">—</span><span v-for="f in row.ReviewFlags" :key="f" class="pcv-sig" :class="pcvSigCls(f)" :title="pcvSigWhy(f)">{{ f }}</span></template>
              </InsTable>
            </div>

            <!-- Table 2 — no basis. Kept separate so nothing here reads as a breach. -->
            <div class="card wide" v-if="pcvNoBasisRows.length">
              <h2>Not Comparable Yet<InsInfoTip term-key="pcv_nobasis" :glossary="fieldTips" mode="click" /></h2>
              <p class="hint">Nothing to measure against, so these are <b>not</b> breaches. These parts are deliberately kept
                out of every count and chart above: this is the first month they were ever costed on this basis. Treat the
                unit cost as unproven rather than wrong. A part with no history here may well have plenty on the other basis.</p>
              <InsTable :columns="pcvNoBasisCols" :rows="pcvNoBasisRows" :glossary="fieldTips" tip-mode="click"
                        sort-key="LatestPeriod" :sort-dir="-1" export-name="mi-part-cost-no-basis"
                        row-noun="parts" :page-size="25" dense
                        :row-class="() => 'clickrow'" @row-click="(r) => pcvOpenPart(r.PartId)">
                <template #cell-PartId="{ row, value }">{{ value }}<div v-if="row.PartDescription" class="cust">{{ row.PartDescription }}</div></template>
                <template #cell-MonthsStale="{ value }">{{ pnum(value) === 0 ? '—' : pInt(value) }}</template>
              </InsTable>
            </div>

            <!-- Drill (inline card, MI style) — level 'parts' then level 'part' -->
            <div class="card wide drill" v-show="pcvDrill.open">
              <div class="drill-head">
                <div>
                  <h2>{{ pcvDrill.title }}</h2>
                  <p class="hint">{{ pcvDrill.hint }}</p>
                </div>
                <button type="button" class="drill-close" @click="pcvDrill.open = false">× close</button>
              </div>

              <p v-if="pcvDrill.loading" class="hint">Loading…</p>

              <!-- level 1: the ranked part list -->
              <template v-else-if="pcvDrill.level === 'parts'">
                <div class="ship-filters">
                  <input type="text" v-model="pcvDrill.q" placeholder="Filter by part or description…" />
                  <span class="ship-count">{{ pcvFilteredDrillRows.length }}<span v-if="pcvDrill.q"> of {{ pcvDrill.rows.length }}</span> part(s)<span v-if="pcvDrill.truncated > 0"> · {{ pInt(pcvDrill.truncated) }} lower-impact not listed</span></span>
                </div>
                <InsTable v-if="pcvFilteredDrillRows.length" :columns="pcvDrillPartCols" :rows="pcvFilteredDrillRows"
                          :glossary="fieldTips" tip-mode="click" :filterable="false" sort-key="ImpactUsd" :sort-dir="-1" dense
                          export-name="mi-part-cost-drill" row-noun="parts"
                          :row-class="() => 'clickrow'"
                          @row-click="(r) => pcvOpenPart(r.PartId, { from: pcvDrill.scope, fromPeriod: pcvDrill.period })">
                  <template #cell-PartId="{ row, value }"><b>{{ value }}</b><span v-if="pcvWatched(row)" class="pcv-star">★</span><div v-if="row.PartDescription" class="cust">{{ row.PartDescription }}</div></template>
                  <template #cell-ChangePct="{ row, value }"><span v-if="!pcvHasBasis(row)" class="pcv-nobasis">no basis</span><span v-else :class="pcvCls(row)">{{ pcvSignedPct(value) }}</span><span v-if="pcvLotShift(row)" class="pcv-lot" :title="pcvCaveat(row)">lot</span></template>
                  <template #cell-MonthsSincePrior="{ value }"><span :class="pnum(value) > 3 ? 'pcv-nobasis' : ''">{{ pInt(value) }}</span></template>
                  <template #cell-Band="{ row, value }"><span class="pcv-chip" :class="pcvChipCls(row)">{{ value }}</span></template>
                </InsTable>
                <p v-else class="hint">No parts match that filter.</p>
              </template>

              <!-- level 2: one part's full history, postings inline underneath -->
              <template v-else-if="pcvDrill.level === 'part'">
                <div class="ship-filters">
                  <button v-if="pcvDrill.fromScope" type="button" class="parts-export" @click="pcvBackToList">‹ Back to the part list</button>
                  <span class="mi-filters-lbl">Basis: {{ pcvBasisLabel() }}</span>
                  <button v-if="pcvDrill.otherBasisMonths > 0" type="button" class="parts-export" @click="pcvSwapBasisForPart">
                    Switch to “{{ pcvBasisLabel(pcvDrill.otherBasis) }}” · {{ pInt(pcvDrill.otherBasisMonths) }} months
                  </button>
                </div>
                <p v-if="pcvDrill.head" class="hint">
                  Latest {{ pcvPeriod(pcvDrill.head.LatestPeriod) }}: <b>{{ pcvUsd4(pcvDrill.head.LatestUnitCost) }}/ea</b>
                  <span v-if="pcvHasBasis(pcvDrill.head)"> · vs {{ pcvPeriod(pcvDrill.head.PriorPeriod) }} {{ pcvUsd4(pcvDrill.head.PriorUnitCost) }}/ea · <span :class="pcvCls(pcvDrill.head)">{{ pcvSignedPct(pcvDrill.head.ChangePct) }}</span></span>
                  <span v-else class="pcv-nobasis"> · first month costed on this basis, nothing to compare</span>
                  · {{ pInt(pcvDrill.head.MonthsPriced) }} month(s) priced
                  · 12-month range {{ pcvUsd4(pcvDrill.head.Min12UnitCost) }}–{{ pcvUsd4(pcvDrill.head.Max12UnitCost) }}
                  <span v-if="pnum(pcvDrill.head.MonthsStale) > 0" class="pcv-nobasis"> · {{ pInt(pcvDrill.head.MonthsStale) }} month(s) behind the newest cost activity</span>
                </p>
                <p v-if="pcvCaveat(pcvDrill.head)" class="hint"><b>Worth knowing:</b> {{ pcvCaveat(pcvDrill.head) }}</p>

                <div v-if="pcvDrill.months.length > 1" class="chartscroll">
                  <h3 class="scrap-col-h">Cost per Piece by Month — full history, not limited by the range toggle</h3>
                  <svg :viewBox="`0 0 ${pcvW} ${pcvH}`" preserveAspectRatio="none" style="height:180px">
                    <polyline :points="pcvPdLine" fill="none" stroke="#c0603a" stroke-width="2" />
                    <circle v-for="(r, i) in pcvDrill.months" :key="'pdc' + i" class="pcv-mark" :class="pcvDotCls(r)"
                            :cx="pcvPdX(i)" :cy="pcvScaleYCost(r.UnitCost)"
                            :r="pcvDrill.txnPeriod === String(r.Period).slice(0,10) ? 4.5 : 3"
                            @click="pcvOpenMonthTxn(r)"><title>{{ pcvPeriod(r.Period) }}: {{ pcvUsd4(r.UnitCost) }}/ea · qty {{ pnumfmt(r.Qty) }} · {{ pInt(r.TxnCount) }} posting(s) · {{ r.ChangePct == null ? 'no prior month' : pcvSignedPct(r.ChangePct) + ' vs ' + pcvPeriod(r.PriorPeriod) }} (click for the postings)</title></circle>
                    <!-- Axes. This history runs to 300+ months for some parts, so ticking every month is
                         hopeless — the two ends date the span and the corner label carries the $ scale. -->
                    <text :x="pcvPad" :y="pcvPad" class="pcv-axlbl">{{ pcvUsd4(pcvPdCostMax) }}/ea</text>
                    <text :x="pcvPad" :y="pcvH - 4" class="pcv-axlbl">{{ pcvPeriod(pcvDrill.months[0].Period) }}</text>
                    <text :x="pcvW - pcvPad" :y="pcvH - 4" text-anchor="end" class="pcv-axlbl">{{ pcvPeriod(pcvDrill.months[pcvDrill.months.length - 1].Period) }}</text>
                  </svg>
                  <p class="hint">Scale starts at $0 so the size of a move reads honestly. Red and green points are months
                    outside the ±{{ pInt(pcvTol) }}% allowance. Click a point for the postings behind it.</p>
                </div>

                <InsTable v-if="pcvDrill.months.length" :columns="pcvMonthCols" :rows="pcvDrill.months"
                          :glossary="fieldTips" tip-mode="click" :filterable="false" sort-key="Period" :sort-dir="-1"
                          dense :page-size="24" export-name="mi-part-cost-history" row-noun="months"
                          :row-class="(r) => (pcvDrill.txnPeriod === String(r.Period).slice(0,10) ? 'matched clickrow' : 'clickrow')"
                          @row-click="pcvOpenMonthTxn">
                  <template #cell-ChangePct="{ row, value }"><span v-if="value == null" class="pcv-nobasis">no prior month</span><span v-else :class="pcvCls(row)">{{ pcvSignedPct(value) }}</span><span v-if="pcvLotShift(row)" class="pcv-lot" :title="pcvCaveat(row)">lot</span></template>
                  <template #cell-Band="{ row, value }"><span class="pcv-chip" :class="pcvChipCls(row)">{{ value }}</span></template>
                </InsTable>
                <p v-else class="hint">No cost activity recorded for this part on this basis.</p>

                <div v-if="pcvDrill.txnPeriod" class="pcv-txn">
                  <h3 class="scrap-col-h">Postings — {{ pcvPeriod(pcvDrill.txnPeriod) }}
                    <button type="button" class="parts-export" @click="pcvDrill.txnPeriod = null; pcvDrill.txn = [];">✕ clear</button>
                  </h3>
                  <p class="hint">Every cost posting that makes up that month's average. One unusual posting can pull a whole month.</p>
                  <p v-if="pcvDrill.txnLoading" class="hint">Loading postings…</p>
                  <InsTable v-else-if="pcvDrill.txn.length" :columns="pcvTxnCols" :rows="pcvDrill.txn"
                            :glossary="fieldTips" tip-mode="click" :filterable="false" sort-key="TxnDate" :sort-dir="1" dense
                            export-name="mi-part-cost-postings" row-noun="postings">
                    <template #cell-UnitCost="{ row, value }"><span :class="pcvTxnOutlier(row) ? 'pcv-over' : ''">{{ pcvUsd4(value) }}</span><span v-if="pcvTxnOutlier(row)" class="pcv-lot" title="More than 25% away from this month's average cost per piece — this is the posting pulling the month.">odd</span></template>
                    <template #cell-VendorOrSource="{ value }"><span v-if="value">{{ value }}</span><span v-else class="pcv-mdim">—</span></template>
                  </InsTable>
                  <p v-else class="hint">
                    No postings retained for this month.<span v-if="pcvDrill.retainedFrom"> Individual postings are kept from
                    {{ pcvPeriod(pcvDrill.retainedFrom) }} onward; the monthly totals above go back further.</span>
                  </p>
                </div>
              </template>
            </div>
          </div>
        </template>
      </section>

      <!-- Data Sources -->
      <section class="page" v-show="activeTab === 'sources'">
        <p class="hint">Every number on this report is pulled in automatically — nothing is typed by hand.
          Here's where each figure comes from, how it gets here, and how current it is.</p>

        <!-- How this report gets its data: one card per source system. -->
        <div class="mi-src-cards">
          <div class="mi-src-card">
            <div class="mi-src-ic" aria-hidden="true">🏭</div>
            <div class="mi-src-body">
              <div class="mi-src-name">Infor VISUAL (VMFG)</div>
              <div class="mi-src-desc">Read straight from VISUAL — the plant's manufacturing system of record — so these match what's posted there. Pulled automatically each night; no manual entry.</div>
              <div class="mi-src-desc">Part Cost Variance reads posted cost on inventory transactions (material, labor, burden and service, divided by the costed quantity). Two bases are kept separate and never blended: <b>sold to customers</b> is what shipped parts cost us, <b>made or bought</b> is work-order and purchase receipts. VISUAL fills these cost fields for <b>closed work orders only</b>, so treat a month's movement as a question to investigate rather than a settled figure.</div>
              <div class="mi-src-feeds">Feeds: Parts Sales · Part Cost Variance · Working Capital</div>
            </div>
          </div>
          <div class="mi-src-card">
            <div class="mi-src-ic" aria-hidden="true">📊</div>
            <div class="mi-src-body">
              <div class="mi-src-name">Manufacturing SharePoint workbooks</div>
              <div class="mi-src-desc">From the Machine Shop and Shipping monthly Excel workbooks in SharePoint. Update the workbook there and it flows into this report on the next nightly sync.</div>
              <div class="mi-src-links">
                <a v-if="srcUrl('machine_shop')" :href="srcUrl('machine_shop')" target="_blank" rel="noopener" class="mi-src-link">Machine Shop workbook ↗</a>
                <a v-if="srcUrl('shipping_otd')" :href="srcUrl('shipping_otd')" target="_blank" rel="noopener" class="mi-src-link">Shipping RTS workbook ↗</a>
              </div>
              <div class="mi-src-feeds">Feeds: Machine Shop $/Hr · Shipping On-Time</div>
            </div>
          </div>
        </div>

        <div class="card wide">
          <h2>Data Sources &amp; Lineage</h2>
          <p class="hint">Every number on this dashboard, its system of record, how it is
            established, and how current it is.</p>
          <div class="chartscroll" v-html="lineageH"></div>
        </div>

        <p v-if="freshness" class="mi-prov-foot">Updates nightly<template v-if="freshness.data_through"> — data through {{ fmtDate(freshness.data_through) }}</template><template v-if="freshness.next_refresh_label">; next update {{ freshness.next_refresh_label }}</template>.</p>
        <p class="mi-prov-help">Need more detail, or think a number looks off? Email <a href="mailto:helpdesk@jcsteele.com">helpdesk@jcsteele.com</a> with the metric name and its “Updated” date, and we'll trace it back to the source for you.</p>
      </section>
    </template>

    <!-- provenance popover (fixed; kept inside the .mi-wrap island) -->
    <div v-show="pop.open" class="mi-prov-pop" :style="pop.style" v-html="pop.html" @click="onPopClick"></div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "./api";
import * as K from "./MiSvgCharts";
import * as XLSX from "xlsx";
import fieldTips from "./fieldTips";
import InsTable from "../Insights/InsTable.vue";
// InsInfoTip is self-styling (scoped block; its one token use has a fallback), so it renders
// correctly inside .mi-wrap even though MI deliberately does not import insights-theme.css.
// InsTable already pulls it in for termKey column headers; this makes it usable directly too.
import InsInfoTip from "../Insights/InsInfoTip.vue";

export default {
  name: "ManufacturingInsights",
  components: { InsTable, InsInfoTip },
  data() {
    return {
      fieldTips,   // plain-English glossary for drill-table column tooltips (title=)
      mode: "live",
      freshness: null,
      win: 999,        // chart range in months (999 = All); narrows the monthly time-series charts
      shipBasis: "both",   // Shipping on-time chart: both | promise | revised
      shipDrill: { open: false, kind: "orders", title: "", hint: "", rows: [],
                   q: "", rep: "", via: "", flag: "", bucket: "", changedOnly: false },
      rmaDrill: { open: false, title: "", hint: "", rows: [],
                  q: "", dept: "", reason: "", type: "", status: "" },
      // Parts drill: two grains — invoice-level (customer/type + export) and part-LINE level
      // (part-family/SKU). Lines are lazy-loaded on first switch to that mode.
      partsDrill: { open: false, mode: "invoices", title: "", year: null, month: null,
                    inv: [], invTotal: 0, invCount: 0, lines: [], linesLoaded: false,
                    q: "", cust: "", type: "", family: "" },
      prov: null,
      canSee: true,
      accessResolved: false,
      activeTab: "machineshop",
      loaded: {},
      tabs: [
        { id: "machineshop", label: "Machine Shop" },
        { id: "shipping", label: "Shipping" },
        { id: "parts", label: "Parts Sales" },
        { id: "rmas", label: "RMAs" },
        { id: "profitability", label: "Profitability" },
        { id: "workingcapital", label: "Working Capital" },
        // Sits with Parts Sales / Profitability / Working Capital because it is a parts-and-cost
        // metric. It is NOT on Work Order Insights: its 'sold' basis is customer shipments, which
        // never touches WORK_ORDER.
        { id: "partcost", label: "Part Cost Variance" },
        { id: "forecast", label: "Forecasting" },
        { id: "sources", label: "Data Sources" },
      ],
      /* ---- Part Cost Variance ----------------------------------------------------------------
         pcvParts is the one potentially large payload (thousands of rows), so the whole tab is
         lazy-loaded by showTab like every other MI tab. Charts here are declarative SVG rather
         than K.*Chart mounts, so there is deliberately no renderTab() entry — Vue re-renders them
         from pcvView/pcvSeries on its own, and that also keeps the drill working inside a card. */
      pcv: null, pcvMonthly: [], pcvSeries: [], pcvParts: [],
      pcvChartError: "",   // non-empty = the two chart panels have no data and why
      pcvLinesQualified: 0, pcvSelectionRule: "", pcvHoverPart: null,
      pcvPartsTruncated: 0, pcvBasis: "sold", pcvWatchOnly: false, pcvBusy: false,
      pcvDrill: { open: false, level: null, title: "", hint: "", loading: false,
                  scope: null, period: null, rows: [], q: "",
                  part: null, head: null, months: [], otherBasis: null, otherBasisMonths: 0,
                  txnPeriod: null, txn: [], txnLoading: false, retainedFrom: null, truncated: 0 },
      // Profitability (Machining/Assembly dept P&L) + Working Capital (live VMFG inventory)
      profitOp: "MACHINING", profit: [], profitLatest: null, profitStmt: null,
      wc: null, wcHead: null, wcByCatProduct: [], wcBridge: [], wcExcluded: [],
      // InsTable column schemas for the Working-Capital tables (sort/filter/CSV).
      wcCatCols: [
        { key: "Category", label: "Category", filter: "text" },
        { key: "PartCount", label: "Parts", num: true, fmt: (v) => this.pInt(v) },
        { key: "InventoryValue", label: "Inventory $", num: true, fmt: (v) => this.pUsd(v) },
        { key: "DeadStockValue", label: "Dead $", num: true, fmt: (v) => this.pUsd(v) },
        { key: "DeadPct", label: "Dead %", num: true, fmt: (v) => this.pPct(v) },
        { key: "WeightedTurns", label: "Turns", num: true, fmt: (v) => this.pTurns(v) },
      ],
      wcExclCols: [
        { key: "part", label: "Part", filter: "text" },
        { key: "descr", label: "Description", filter: "text" },
        { key: "reason", label: "Excluded because", filter: "select", termKey: "wc_exclusion_reason" },
        { key: "product", label: "Product", filter: "select" },
        { key: "commodity", label: "Commodity", filter: "select" },
        { key: "onHandQty", label: "On hand", num: true, fmt: (v, r) => this.pnumfmt(v) + (r.um ? " " + r.um : "") },
        { key: "unitCost", label: "Unit $", num: true, fmt: (v) => this.pUsd2(v) },
        { key: "onHandValue", label: "Value", num: true, fmt: (v) => this.pUsd(v) },
        { key: "annualUsage", label: "Annual usage", num: true, fmt: (v) => this.pnumfmt(v) },
        { key: "idleYears", label: "Years idle", num: true, termKey: "wc_idle_years", fmt: (v) => (v == null ? "never moved" : this.pnum(v).toFixed(1)) },
      ],
      wcSlowCols: [
        { key: "part", label: "Part", filter: "text" },
        { key: "descr", label: "Description", filter: "text" },
        { key: "product", label: "Product", filter: "select" },
        { key: "onHandQty", label: "On hand", num: true, fmt: (v, r) => this.pnumfmt(v) + (r.um ? " " + r.um : "") },
        { key: "unitCost", label: "Unit $", num: true, fmt: (v) => this.pUsd2(v) },
        { key: "onHandValue", label: "Value", num: true, fmt: (v) => this.pUsd(v) },
        { key: "annualUsage", label: "Annual usage", num: true, fmt: (v) => this.pnumfmt(v) },
        { key: "bucket", label: "Bucket", filter: "select" },
      ],
      ovCards: [],
      forecastCards: [],
      lateHtml: '<div class="hint">Loading…</div>',
      lineageH: '<div class="hint">Loading…</div>',
      rmaTopH: '<div class="hint">Loading…</div>',
      rmaNote: "",
      // raw datasets kept for drill context
      _ms: [], _parts: [], _ship: [], _shipInt: [], _shipLateMonthly: [], _rmas: [],
      drill: { open: false, title: "", hint: "", kind: "chart", html: "" },
      pop: { open: false, html: "", style: {} },
    };
  },
  computed: {
    /* ===================== Part Cost Variance ==========================================
       Own chart geometry on purpose. MI's other charts are drawn imperatively by MiSvgCharts
       into $ref mounts; these three are declarative SVG because the ±10% band is a drawn
       object with shape-encoded marks, which is far clearer expressed in the template — and
       the design system explicitly leaves chart markup to the report. Uses MI's tail() for
       the 6/12/All window so the existing mi-seg toggle drives it with no extra wiring. */
    pcvView() { return this.tail(this.pcvMonthly); },
    pcvLatest() { return this.pcvMonthly.length ? this.pcvMonthly[this.pcvMonthly.length - 1] : null; },
    pcvLatestLabel() { return this.pcvLatest ? this.pcvMoLabel(this.pcvLatest) : ""; },
    pcvTol() { return this.pnum((this.pcv && this.pcv.tolerance_pct) || 10); },
    /* Mirrors the server's PcMaxGapMonths. Beyond this the comparison reaches back far enough that
       the move is drift (often just inflation) rather than a jump, which is why it reads as a caveat
       below rather than as a signal. Measured reality on the sold basis: 109 of 188 flagged parts
       compare back more than 3 months, so this is the norm and not an edge case. */
    pcvMaxGap() { return this.pnum((this.pcv && this.pcv.max_gap_months) || 3); },
    /* Most-worth-a-look first. pcvSignalRank turns a row's first match into its queue position.
       The first pcvTrueSignals entries are reasons to LOOK; the remainder are reasons the
       percentage may be MISLEADING. Band already carries the monthly breach, so it is not
       repeated here. Values must not be substrings of one another — InsTable's select filter
       matches with String.includes(), the same constraint the Band strings already respect. */
    pcvSignalOrder() {
      return ["Quietly drifting", "Stale cost", "Trend run", "Old baseline", "Lot size shift", "Thin history"];
    },
    pcvTrueSignals() { return 3; },
    pcvW() { return 720; },
    pcvH() { return 200; },
    pcvPad() { return 16; },
    pcvStep() { return (this.pcvW - this.pcvPad * 2) / Math.max(this.pcvView.length, 1); },
    pcvGrpW() { return this.pcvStep * 0.34; },
    // Taller than the other charts: 12 overlaid lines need the vertical room to stay separable.
    pcvLineH() { return 260; },
    /* Index domain. Floors at 100±25 so the ±10% band stays a legible object when everything is
       calm, and clamps at the p90 of the drawn points so one 930% part (they exist in this data —
       NDK D 1200 HS) cannot flatten every other line onto the 100 mark. Points past the domain are
       drawn at the edge with a larger dot and keep their real number in the tooltip. */
    pcvIdxBounds() {
      const v = [];
      this.pcvSeries.forEach(r => { const x = this.pnum(r.IdxCost); if (x > 0) v.push(x); });
      if (!v.length) return { lo: 75, hi: 125 };
      const sorted = v.slice().sort((a, b) => a - b);
      const p90 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))];
      const p10 = sorted[Math.max(0, Math.floor(sorted.length * 0.1))];
      const hi = Math.max(100 + Math.max(25, this.pcvTol * 1.5), Math.ceil(p90 / 5) * 5);
      const lo = Math.min(100 - Math.max(25, this.pcvTol * 1.5), Math.floor(p10 / 5) * 5);
      return { lo: Math.max(0, lo), hi };
    },
    pcvBreachMax() {
      return Math.max(1, ...this.pcvView.map(m => Math.max(this.pnum(m.OverCount), this.pnum(m.UnderCount))));
    },
    /* One entry per part = one polyline. Built from pcvSeries, which the server guarantees is the
       SAME parts across every month — a per-month "top N" cut cannot make a line, because its
       membership changes and consecutive points would belong to different parts.

       A part with no activity in a month simply has no point there; the line joins across the gap
       rather than dropping to zero, and the tooltip states the gap in months so a straight segment
       spanning a quiet period is never read as a flat cost. */
    pcvLines() {
      const idx = {};
      this.pcvView.forEach((m, i) => { idx[String(m.Period).slice(0, 10)] = i; });
      const byPart = {};
      this.pcvSeries.forEach(r => {
        const k = String(r.Period).slice(0, 10);
        if (idx[k] == null) return;                                  // outside the 6/12/All window
        if (this.pcvWatchOnly && !this.pcvWatched(r)) return;
        (byPart[r.PartId] = byPart[r.PartId] || []).push({ r, i: idx[k] });
      });
      const b = this.pcvIdxBounds;
      const out = [];
      Object.keys(byPart).forEach(part => {
        const rows = byPart[part].sort((x, y) => x.i - y.i);
        if (rows.length < 2) return;                                 // a single dot is not a line
        // Re-base to the first month still visible after the window/watchlist filters, so the
        // line always starts at 100 on screen no matter which window the user picked.
        const base = this.pnum(rows[0].r.UnitCost);
        if (!(base > 0)) return;
        const pts = rows.map(({ r, i }) => {
          const v = (this.pnum(r.UnitCost) / base) * 100;
          return {
            row: r, idx: v, x: this.pcvX(i), y: this.pcvIdxY(v),
            clamped: v > b.hi || v < b.lo,
          };
        });
        const last = pts[pts.length - 1];
        out.push({
          PartId: part,
          short: part.length > 13 ? part.slice(0, 12) + "…" : part,
          watched: this.pcvWatched(rows[0].r),
          color: this.pcvLineColor(out.length),
          pts,
          points: pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" "),
          end: last, endIdx: last.idx,
          cls: this.pcvWatched(rows[0].r) ? "pcv-line-watch" : "",
        });
      });
      // Biggest end-of-window mover first, so the legend reads as a ranked list.
      return out.sort((x, y) => Math.abs(y.endIdx - 100) - Math.abs(x.endIdx - 100));
    },
    pcvAnyClamped() { return this.pcvLines.some(l => l.pts.some(p => p.clamped)); },
    // Watchlist comes from the API so the cost team's list can change without a frontend deploy.
    // Keys are whitespace-normalised + upper-cased: VISUAL PART_ID is CHAR and arrives padded.
    pcvWatchSet() {
      const s = {};
      this.arr(this.pcv && this.pcv.watchlist).forEach(p => { s[this.pcvKey(p)] = true; });
      return s;
    },
    pcvWatchCount() { return this.pcvParts.filter(r => this.pcvWatched(r)).length; },
    /* The review signals are attached to the ROW rather than computed in the cell, because InsTable
       sorts, filters and exports the raw row value — a slot-only badge would be invisible to all
       three. ReviewFlag is the primary (most serious) signal so the select filter has one clean
       option per signal; ReviewFlags keeps the full list for the badges.

       Note the server already returns these rows ranked by ImpactUsd DESC, and Array#sort is stable,
       so sorting by ReviewRank alone yields severity-major / dollars-minor for free — which is
       exactly the queue order wanted, with no composite sort key. */
    pcvRows() {
      const base = this.pcvParts.filter(r => this.pcvHasBasis(r));
      const scoped = this.pcvWatchOnly ? base.filter(r => this.pcvWatched(r)) : base;
      return scoped.map(r => {
        const sig = this.pcvSignals(r);
        return Object.assign({}, r, {
          ReviewFlag: sig.length ? sig[0] : "Not flagged",
          ReviewFlags: sig,
          ReviewRank: this.pcvSignalRank(r),
        });
      });
    },
    pcvNoBasisRows() {
      const base = this.pcvParts.filter(r => !this.pcvHasBasis(r));
      return this.pcvWatchOnly ? base.filter(r => this.pcvWatched(r)) : base;
    },
    // Ranked by DOLLARS, not percent — a 500% move on a 50-cent bolt is not the story.
    pcvMovers() {
      return this.pcvRows.slice()
        .sort((a, b) => Math.abs(this.pnum(b.ImpactUsd)) - Math.abs(this.pnum(a.ImpactUsd)))
        .slice(0, 12);
    },
    pcvMoverMax() { return Math.max(15, ...this.pcvMovers.map(r => Math.abs(this.pnum(r.ChangePct)))); },
    pcvTolStyle() { const w = (this.pcvTol / this.pcvMoverMax) * 50; return { left: (50 - w) + "%", width: (w * 2) + "%" }; },
    pcvFilteredDrillRows() {
      const rows = this.arr(this.pcvDrill.rows);
      const q = (this.pcvDrill.q || "").trim().toUpperCase();
      if (!q) return rows;
      return rows.filter(r => (r.PartId || "").toUpperCase().indexOf(q) >= 0
                           || (r.PartDescription || "").toUpperCase().indexOf(q) >= 0);
    },
    pcvTxnAvg() {
      const t = this.arr(this.pcvDrill.txn);
      const q = t.reduce((s, r) => s + this.pnum(r.Qty), 0);
      return q > 0 ? t.reduce((s, r) => s + this.pnum(r.ExtCost), 0) / q : 0;
    },
    pcvPdCostMax() { return Math.max(0.0001, ...this.arr(this.pcvDrill.months).map(r => this.pnum(r.UnitCost))); },
    pcvPdLine() {
      return this.arr(this.pcvDrill.months)
        .map((r, i) => `${this.pcvPdX(i).toFixed(1)},${this.pcvScaleYCost(this.pnum(r.UnitCost)).toFixed(1)}`).join(" ");
    },

    // InsTable column schemas. `fmt` is display-only — InsTable sorts, filters and exports the
    // RAW row value, so Period stays an ISO date and ChangePct stays a number in percent units.
    pcvCols() {
      return [
        { key: "PartId", label: "Part", filter: "text", termKey: "part" },
        /* The review lens sits second, NOT at the end of the row. This table is 19 columns wide and
           lives in an overflow-x scroller, so anything past about the tenth column is off-screen on a
           normal window — including, if it were last, the very column the default sort is applied to,
           which would leave the queue order looking arbitrary with no visible sort arrow.
           Band says WHETHER the month broke the allowance; this says whether the row is worth a
           person's time, which is a different question and the one the cost team reviews on. */
        { key: "ReviewFlag", label: "Why flagged", filter: "select", termKey: "pcv_reviewflag" },
        // Kept beside it so the queue's sort arrow is visible. Hideable via Columns; low value to read.
        { key: "ReviewRank", label: "Look first", num: true, filter: "number", termKey: "pcv_reviewrank" },
        { key: "CommodityCode", label: "Commodity", filter: "select", termKey: "pcv_commodity" },
        { key: "LatestPeriod", label: "Latest month", termKey: "pcv_period", fmt: v => this.pcvPeriod(v) },
        { key: "LatestUnitCost", label: "Unit cost", num: true, filter: "number", termKey: "pcv_unitcost", fmt: v => this.pcvUsd4(v) },
        { key: "PriorPeriod", label: "Compared to", termKey: "pcv_priorperiod", fmt: v => this.pcvPeriod(v) },
        { key: "PriorUnitCost", label: "Prior cost", num: true, termKey: "pcv_priorcost", fmt: v => this.pcvUsd4(v) },
        { key: "MonthsSincePrior", label: "Gap (mo)", num: true, filter: "number", termKey: "pcv_gap" },
        { key: "ChangePct", label: "Change", num: true, filter: "number", termKey: "pcv_changepct" },
        { key: "ChangeUsd", label: "Change $/ea", num: true, termKey: "pcv_changeusd" },
        /* The trend columns. Filterable on number so "show me everything up more than 15% over six
           months" is a column filter rather than an export into Excel. Sorting uses the raw value,
           so a NULL (not enough history) sorts apart from a genuine 0.0%. */
        { key: "Change3MoPct", label: "3-mo change", num: true, filter: "number", termKey: "pcv_change3mo" },
        { key: "Change6MoPct", label: "6-mo change", num: true, filter: "number", termKey: "pcv_change6mo" },
        { key: "TrendRun", label: "Run (mo)", num: true, filter: "number", termKey: "pcv_trendrun" },
        { key: "ImpactUsd", label: "Impact $", num: true, filter: "number", termKey: "pcv_impactcol", fmt: v => this.pUsd(v) },
        { key: "LatestQty", label: "Qty", num: true, termKey: "pcv_qty", fmt: v => this.pnumfmt(v) },
        { key: "MonthsPriced", label: "Months", num: true, filter: "number", termKey: "pcv_months", fmt: v => this.pInt(v) },
        { key: "MonthsStale", label: "Months behind", num: true, filter: "number", termKey: "pcv_stale" },
        { key: "Band", label: "Status", filter: "select", termKey: "pcv_band" },
      ];
    },
    pcvNoBasisCols() {
      return [
        { key: "PartId", label: "Part", filter: "text", termKey: "part" },
        { key: "CommodityCode", label: "Commodity", filter: "select", termKey: "pcv_commodity" },
        { key: "LatestPeriod", label: "First / only month", termKey: "pcv_period", fmt: v => this.pcvPeriod(v) },
        { key: "LatestUnitCost", label: "Unit cost", num: true, termKey: "pcv_unitcost", fmt: v => this.pcvUsd4(v) },
        { key: "LatestQty", label: "Qty", num: true, termKey: "pcv_qty", fmt: v => this.pnumfmt(v) },
        { key: "TxnCount", label: "Postings", num: true, termKey: "pcv_txncount", fmt: v => this.pInt(v) },
        { key: "MonthsStale", label: "Months behind", num: true, termKey: "pcv_stale" },
      ];
    },
    pcvDrillPartCols() {
      return [
        { key: "PartId", label: "Part", termKey: "part" },
        { key: "LatestPeriod", label: "Month", termKey: "pcv_period", fmt: v => this.pcvPeriod(v) },
        { key: "PriorUnitCost", label: "Prior $/ea", num: true, termKey: "pcv_priorcost", fmt: v => this.pcvUsd4(v) },
        { key: "LatestUnitCost", label: "Now $/ea", num: true, termKey: "pcv_unitcost", fmt: v => this.pcvUsd4(v) },
        { key: "MonthsSincePrior", label: "Gap (mo)", num: true, termKey: "pcv_gap" },
        { key: "ChangePct", label: "Change", num: true, termKey: "pcv_changepct" },
        { key: "ImpactUsd", label: "Impact $", num: true, termKey: "pcv_impactcol", fmt: v => this.pUsd(v) },
        { key: "LatestQty", label: "Qty", num: true, termKey: "pcv_qty", fmt: v => this.pnumfmt(v) },
        { key: "Band", label: "Status", termKey: "pcv_band" },
      ];
    },
    pcvMonthCols() {
      return [
        { key: "Period", label: "Month", termKey: "pcv_period", fmt: v => this.pcvPeriod(v) },
        { key: "UnitCost", label: "Unit cost", num: true, termKey: "pcv_unitcost", fmt: v => this.pcvUsd4(v) },
        { key: "ChangePct", label: "Change", num: true, termKey: "pcv_changepct" },
        { key: "PriorPeriod", label: "Compared to", termKey: "pcv_priorperiod", fmt: v => this.pcvPeriod(v) },
        { key: "Qty", label: "Qty", num: true, termKey: "pcv_qty", fmt: v => this.pnumfmt(v) },
        { key: "AvgLotQty", label: "Avg lot", num: true, termKey: "pcv_lot", fmt: v => this.pnumfmt(v) },
        { key: "ExtCost", label: "Total $", num: true, termKey: "pcv_extcost", fmt: v => this.pUsd(v) },
        { key: "TxnCount", label: "Postings", num: true, termKey: "pcv_txncount", fmt: v => this.pInt(v) },
        { key: "Band", label: "Status", termKey: "pcv_band" },
      ];
    },
    pcvTxnCols() {
      return [
        { key: "TxnDate", label: "Date", termKey: "pcv_txndate" },
        { key: "TxnType", label: "Type", termKey: "pcv_txntype" },
        { key: "SourceId", label: "WO / PO / Order", termKey: "pcv_txnsource" },
        { key: "Qty", label: "Qty", num: true, termKey: "pcv_qty", fmt: v => this.pnumfmt(v) },
        { key: "UnitCost", label: "Unit cost", num: true, termKey: "pcv_txnunit" },
        { key: "ExtCost", label: "Total $", num: true, termKey: "pcv_extcost", fmt: v => this.pUsd(v) },
        { key: "VendorOrSource", label: "Vendor", termKey: "pcv_txnvendor" },
      ];
    },

    // WIP excluding burden. Accounting's Crystal report hasn't been tied to yet and burden is the
    // suspected difference, so both figures are shown until the basis is confirmed.
    wcWipExBurden() {
      const h = this.wcHead; if (!h) return 0;
      return this.pnum(h.WipMaterialValue) + this.pnum(h.WipLaborValue) + this.pnum(h.WipServiceValue);
    },
    modeTitle() {
      return this.mode === "live"
        ? "auto-refreshing from the nightly VMFG copy"
        : "static snapshot";
    },
    // Latest month's hidden-lateness (on-time vs adjusted date but late vs original promise).
    shipGapLatest() {
      const s = this._shipInt || [];
      if (!s.length) return null;
      const r = s[s.length - 1];
      return { pct: r.HiddenLatePct, count: r.HiddenLateCount, year: r.Year, month: r.MonthNum };
    },
    // Speed-mix legend swatches, kept in sync with renderShipSpeed()'s series colors.
    legendColors() {
      return {
        nextday: K.C.good, twothree: K.C.accent,
        fourfourteen: K.C.warn, overfourteen: K.C.bad,
      };
    },
    // Distinct filter values from the loaded month's order rows.
    shipDrillFacets() {
      const rows = this.shipDrill.rows || [];
      const uniq = k => Array.from(new Set(rows.map(r => r[k]).filter(v => v != null && v !== ""))).sort();
      return { reps: uniq("SalesRepName"), vias: uniq("ShipVia"), flags: uniq("OnTimeVsPromise"), buckets: uniq("ShipSpeedBucket") };
    },
    shipDrillFiltered() {
      const d = this.shipDrill, q = (d.q || "").toLowerCase();
      return (d.rows || []).filter(r => {
        if (d.rep && r.SalesRepName !== d.rep) return false;
        if (d.via && r.ShipVia !== d.via) return false;
        if (d.flag && r.OnTimeVsPromise !== d.flag) return false;
        if (d.bucket && r.ShipSpeedBucket !== d.bucket) return false;
        if (d.changedOnly && !(r.NumberOfChanges > 0)) return false;
        if (q && ((r.OrderID || "") + " " + (r.CustomerName || "")).toLowerCase().indexOf(q) === -1) return false;
        return true;
      });
    },
    // Column schema for the Shipping drill (InsTable). External ship-filters still feed shipDrillFiltered;
    // InsTable adds column sort + bubbles + CSV. Status sorts on OnTimeVsPromise; the cell shows IntegrityFlag||it.
    shipDrillCols() {
      return [
        { key: "OrderID", label: "Order", termKey: "order" },
        { key: "CustomerName", label: "Customer", cls: "cust", termKey: "customer" },
        { key: "SalesRepName", label: "Rep", termKey: "rep" },
        { key: "ShipVia", label: "Ship-Via", termKey: "ship_via" },
        { key: "OriginalPromiseDate", label: "Original Promise", termKey: "orig_promise" },
        { key: "RevisedDate", label: "Revised", termKey: "revised" },
        { key: "ActualShipDate", label: "Actual Ship", termKey: "actual_ship" },
        { key: "DaysLateVsPromise", label: "Late vs Promise", num: true, termKey: "late_promise" },
        { key: "DaysPushedOut", label: "Days Pushed", num: true, termKey: "days_pushed" },
        { key: "NumberOfChanges", label: "Changes", num: true, termKey: "changes" },
        { key: "LastChangedBy", label: "Changed By", termKey: "changed_by" },
        { key: "OnTimeVsPromise", label: "Status", termKey: "status" },
      ];
    },
    // Distinct filter values from the loaded month's RMA rows (blanks excluded).
    rmaDrillFacets() {
      const rows = this.rmaDrill.rows || [];
      const uniq = k => Array.from(new Set(rows.map(r => r[k]).filter(v => v != null && v !== ""))).sort();
      return { depts: uniq("department"), reasons: uniq("reason"), types: uniq("type"), statuses: uniq("status") };
    },
    rmaDrillFiltered() {
      const d = this.rmaDrill, q = (d.q || "").toLowerCase();
      return (d.rows || []).filter(r => {
        if (d.dept && r.department !== d.dept) return false;
        if (d.reason && r.reason !== d.reason) return false;
        if (d.type && r.type !== d.type) return false;
        if (d.status && r.status !== d.status) return false;
        if (q && ((r.rma || "") + " " + (r.customer || "") + " " + (r.part || "") + " " + (r.partDesc || "")).toLowerCase().indexOf(q) === -1) return false;
        return true;
      });
    },
    // Column schema for the RMA drill (InsTable). External rma filters feed rmaDrillFiltered. Dropped the
    // positional "#" column (a static counter doesn't make sense once rows are sortable).
    rmaDrillCols() {
      return [
        { key: "rma", label: "RMA", termKey: "rma_no" },
        { key: "date", label: "Date", termKey: "date" },
        { key: "customer", label: "Customer", cls: "cust", termKey: "customer" },
        { key: "part", label: "Part", termKey: "part" },
        { key: "qty", label: "Qty", num: true, termKey: "qty", fmt: (v) => K.num1(v) },
        { key: "reason", label: "Reason for return", cls: "cust", termKey: "reason" },
        { key: "department", label: "Dept", termKey: "dept" },
        { key: "type", label: "Type", termKey: "rma_type" },
        { key: "status", label: "Status", termKey: "status" },
      ];
    },
    // Parts drill facets — mode-aware. Family collapses null/blank to "(unclassified)".
    partsFacets() {
      const d = this.partsDrill;
      const uniq = (arr, k) => Array.from(new Set(arr.map(r => r[k]).filter(v => v != null && v !== ""))).sort();
      if (d.mode === "lines") {
        const fams = Array.from(new Set((d.lines || []).map(r => (r.family == null || r.family === "") ? "(unclassified)" : r.family))).sort();
        return { custs: uniq(d.lines, "customer"), types: uniq(d.lines, "type"), families: fams };
      }
      return { custs: uniq(d.inv, "customer"), types: uniq(d.inv, "type"), families: [] };
    },
    partsFiltered() {
      const d = this.partsDrill, q = (d.q || "").toLowerCase();
      if (d.mode === "lines") {
        return (d.lines || []).filter(r => {
          if (d.cust && r.customer !== d.cust) return false;
          if (d.type && r.type !== d.type) return false;
          if (d.family) {
            const fam = (r.family == null || r.family === "") ? "(unclassified)" : r.family;
            if (fam !== d.family) return false;
          }
          if (q && ((r.invoice || "") + " " + (r.customer || "") + " " + (r.part || "") + " " + (r.partDesc || "")).toLowerCase().indexOf(q) === -1) return false;
          return true;
        });
      }
      return (d.inv || []).filter(r => {
        if (d.cust && r.customer !== d.cust) return false;
        if (d.type && r.type !== d.type) return false;
        if (q && ((r.invoice || "") + " " + (r.customer || "")).toLowerCase().indexOf(q) === -1) return false;
        return true;
      });
    },
    // Net $ of the currently-filtered rows (credit memos are negative → true net).
    partsFilteredTotal() {
      return this.partsFiltered.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    },
    // Parts drill column schemas (InsTable) — mode-aware. External parts filters feed partsFiltered; Excel
    // export stays on the toolbar. Dropped the positional "#" column (static counter vs sortable rows).
    partsInvCols() {
      return [
        { key: "invoice", label: "Invoice", termKey: "invoice" },
        { key: "date", label: "Date", termKey: "date" },
        { key: "customer", label: "Customer", cls: "cust", termKey: "customer" },
        { key: "amount", label: "Amount", num: true, termKey: "amount", fmt: (v) => K.usd0(Number(v)) },
      ];
    },
    partsLineCols() {
      return [
        { key: "invoice", label: "Invoice", termKey: "invoice" },
        { key: "date", label: "Date", termKey: "date" },
        { key: "customer", label: "Customer", cls: "cust", termKey: "customer" },
        { key: "part", label: "Part", termKey: "part" },
        { key: "family", label: "Family", termKey: "family", fmt: (v) => (v == null || v === "") ? "(unclassified)" : v },
        { key: "qty", label: "Qty", num: true, termKey: "qty", fmt: (v) => K.num1(v) },
        { key: "amount", label: "Amount", num: true, termKey: "amount", fmt: (v) => K.usd0(Number(v)) },
      ];
    },
  },
  mounted() {
    this._onDocClick = (e) => {
      if (!this.pop.open) return;
      const inPop = e.target.closest && e.target.closest(".mi-prov-pop");
      const isBtn = e.target.classList && e.target.classList.contains("info-btn");
      if (!inPop && !isBtn) this.hidePop();
    };
    document.addEventListener("click", this._onDocClick);
    this._onResize = () => this.hidePop();
    window.addEventListener("resize", this._onResize);
    this.checkAccess().then(() => {
      if (!this.canSee) return;
      this.loadHeader();
      this.loadOverview();      // top KPI strip — always shown, not tab-gated
      this.showTab("machineshop");
    });
  },
  beforeDestroy() {
    document.removeEventListener("click", this._onDocClick);
    window.removeEventListener("resize", this._onResize);
  },
  methods: {
    hasProv(key) {
      return !!(this.prov && this.prov.metrics && this.prov.metrics.some(m => m.key === key));
    },
    // SharePoint library URL for a metric (from its provenance source_url), if any.
    srcUrl(key) {
      const m = this.prov && this.prov.metrics ? this.prov.metrics.find(x => x.key === key) : null;
      return m && m.source_url ? m.source_url : null;
    },
    arr(x) { return Array.isArray(x) ? x : []; },
    async checkAccess() {
      try {
        const a = await api.accessMine();
        const keys = (a && a.authorizedReports) || [];
        this.canSee = keys.indexOf("manufacturing-insights") !== -1;
      } catch (e) {
        this.canSee = true; // fail open; the backend [Authorize] still guards the data
      } finally {
        this.accessResolved = true;
      }
    },
    async loadHeader() {
      try { const h = await api.health(); this.mode = h && h.mode ? h.mode : "live"; } catch (e) { /* */ }
      try { this.freshness = await api.freshness(); } catch (e) { this.freshness = null; }
      try {
        this.prov = await api.provenance();
        this.lineageH = K.lineageHtml(this.prov);
      } catch (e) { /* */ }
    },
    // Short date for the freshness pill (e.g. "Jul 16"); accepts a yyyy-MM-dd string.
    fmtDate(s) {
      if (!s) return "—";
      const d = new Date(s + "T00:00:00");
      return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    },
    // Chart-range filter: render the trailing `win` months of a monthly series.
    tail(a) { return (Array.isArray(a) && this.win < a.length) ? a.slice(-this.win) : (a || []); },
    setWin(n) { this.win = n; this.$nextTick(() => this.renderTab(this.activeTab)); },
    showTab(id) {
      this.activeTab = id;
      if (!this.loaded[id]) { this.$set(this.loaded, id, true); this.loadTab(id); }
      else this.$nextTick(() => this.renderTab(id));
    },
    async loadTab(id) {
      try {
        if (id === "forecast") { this.forecastCards = this.arr(await api.forecast()); this.$nextTick(() => this.renderForecast()); }
        else if (id === "machineshop") { this._ms = this.arr(await api.machineshop()); this.$nextTick(() => this.renderMs()); }
        else if (id === "shipping") {
          const [si, lt, lm] = await Promise.all([api.shippingIntegrity(), api.shippingLate(), api.shippingLateMonthly()]);
          this._shipInt = this.arr(si); this.lateHtml = K.lateShipTableHtml(this.arr(lt)); this._shipLateMonthly = this.arr(lm);
          this.$nextTick(() => this.renderShip());
        }
        else if (id === "parts") { this._parts = this.arr(await api.partsSales()); this.$nextTick(() => this.renderParts()); }
        else if (id === "rmas") {
          const [rm, rt] = await Promise.all([api.rma(), api.rmaTop()]);
          this._rmas = this.arr(rm); this.rmaTopH = K.rmaTopHtml(rt);
          this.$nextTick(() => this.renderRmas());
        }
        else if (id === "profitability") { await this.loadProfitability(); }
        else if (id === "workingcapital") {
          const res = await api.workingCapital(); this.wc = res || null;
          this.wcHead = this.arr(res && res.headline).slice(-1)[0] || null;
          this.wcByCatProduct = this.arr(res && res.by_category).filter(r => r.CategoryType === "PRODUCT");
          this.wcBridge = this.arr(res && res.bridge);
          this.wcExcluded = this.arr(res && res.excluded_parts);
        }
        else if (id === "partcost") { await this.loadPartCost(); }
        else if (id === "sources") { if (!this.prov) { this.prov = await api.provenance(); this.lineageH = K.lineageHtml(this.prov); } }
      } catch (e) { /* graceful: mounts keep their Loading/awaiting hint */ }
    },
    /* ---- Part Cost Variance ----------------------------------------------------------------- */
    // One loader for both the initial visit and a basis switch. 'sold' and 'made' are genuinely
    // different questions, so nothing is cached across the switch.
    async loadPartCost() {
      this.pcvBusy = true;
      try {
        const [pc, pcm, pcp] = await Promise.all([
          api.partCost(this.pcvBasis).catch(() => null),
          api.partCostMonthly(this.pcvBasis).catch(() => null),
          api.partCostParts(this.pcvBasis, "all").catch(() => null),
        ]);
        /* `notReady` alone is NOT enough. A timed-out query answers HTTP 200 with
           {queryError:true, message:...}; that body passes a notReady-only test, `.months` and
           `.series` then come back undefined, arr() turns them into [], and both charts disappear
           behind their v-if with nothing shown. That is exactly how the trend chart stayed broken
           from release until someone finally opened the tab on 2026-08-12. Treat a payload as
           usable only if it is neither notReady nor queryError, and say so when it is not. */
        const usable = (r) => !!r && !r.notReady && !r.queryError;
        this.pcv = usable(pc) ? pc : null;
        this.pcvMonthly = usable(pcm) ? this.arr(pcm.months) : [];
        this.pcvSeries = usable(pcm) ? this.arr(pcm.series) : [];
        this.pcvChartError = usable(pcm)
          ? ""
          : !pcm            ? "The request failed before it returned."
          : pcm.queryError  ? (pcm.message || "The query timed out.")
          : /* notReady */    "The snapshot tables are not present on the server yet.";
        this.pcvLinesQualified = this.pnum(pcm && pcm.lines_qualified);
        this.pcvSelectionRule = (pcm && pcm.selection_rule) || "moved outside the allowance";
        this.pcvHoverPart = null;
        this.pcvParts = pcp && !pcp.notReady ? this.arr(pcp.rows) : [];
        this.pcvPartsTruncated = this.pnum(pcp && pcp.truncated);
      } finally { this.pcvBusy = false; }
    },
    async pcvSetBasis(b) {
      if (b === this.pcvBasis || this.pcvBusy) return;
      this.pcvBasis = b;
      this.pcvDrill.open = false;
      await this.loadPartCost();
    },
    // ---- geometry ----
    pcvX(i) { return this.pcvPad + this.pcvStep * i + this.pcvStep / 2; },
    pcvGrpX(i, s) { return this.pcvX(i) - this.pcvGrpW - 1 + s * (this.pcvGrpW + 2); },
    /* index value (100 = where the part started) -> y, clamped to the visible domain.
       Note the axis is NOT zero-based, unlike pcvScaleYCost below: this scale shows relative
       movement, and anchoring it at zero would squash every line into the top of the box. */
    pcvIdxY(v) {
      const top = this.pcvPad, bot = this.pcvLineH - this.pcvPad;
      const b = this.pcvIdxBounds;
      const span = Math.max(1, b.hi - b.lo);
      const c = Math.max(0, Math.min(1, (this.pnum(v) - b.lo) / span));
      return bot - c * (bot - top);
    },
    /* Line colours. Deliberately not the report's red/green semantic pair — here colour identifies
       WHICH part, and direction is carried by the line's shape against the band. Ordered for
       maximum separation between neighbours and checked to stay distinguishable on the dark card. */
    pcvLineColor(i) {
      const p = ["#9e2436", "#2f6f9e", "#2f7a4f", "#b5761f", "#6b4c9a", "#0f8a8a",
                 "#c2506a", "#4a7a2f", "#8a5a2b", "#3f5c8a", "#a03f8a", "#5a6b76"];
      return p[i % p.length];
    },
    // absolute cost -> y, zero-based so the size of a move reads honestly
    pcvScaleYCost(v) {
      const h = this.pcvH - this.pcvPad;
      return this.pcvPad + (h - this.pcvPad) * (1 - this.pnum(v) / this.pcvPdCostMax);
    },
    pcvBarY(v) {
      const h = this.pcvH - this.pcvPad;
      return this.pcvPad + (h - this.pcvPad) * (1 - this.pnum(v) / this.pcvBreachMax);
    },
    pcvBarH(v) { return (this.pcvH - this.pcvPad) - this.pcvBarY(v); },
    pcvBarTop(m) { return this.pcvBarY(Math.max(this.pnum(m.OverCount), this.pnum(m.UnderCount))) - 5; },
    /* Point tooltip. Says all four things a reader needs and can't infer from the shape: where the
       cost actually is in dollars, how far it has moved SINCE THE START of the window (the line's
       own measure), how far it moved from the previous month (the table's measure — different
       number, so it is labelled), and any gap or caveat behind the segment. */
    pcvPtTitle(ln, p) {
      const r = p.row;
      const bits = [`${ln.PartId}${ln.watched ? " ★" : ""} — ${this.pcvMoLabel(r)}`];
      bits.push(`${this.pcvUsd4(r.UnitCost)}/pc  (${this.pcvSignedPct(p.idx - 100)} vs its first month here)`);
      if (this.pcvHasBasis(r)) {
        const gap = this.pnum(r.MonthsSincePrior);
        bits.push(`month over month: ${this.pcvSignedPct(r.ChangePct)}`
          + (gap > 1 ? ` — but the month before that was ${gap} months back, not last month` : ""));
      } else {
        bits.push("month over month: no basis — nothing earlier to compare against");
      }
      const run = this.pnum(r.TrendRun);
      if (Math.abs(run) >= 2) bits.push(`${Math.abs(run)} months running in the same direction`);
      if (this.pnum(r.SlowDrift) === 1) bits.push(`drifted past ±${this.pInt(this.pcvTol)}% over 6 months without any single month tripping it`);
      if (p.clamped) bits.push("past the edge of the axis — the number above is the real one");
      if (r.QualityNote) bits.push(String(r.QualityNote));
      return bits.join("\n");
    },
    pcvPdX(i) {
      const n = Math.max(this.arr(this.pcvDrill.months).length, 1);
      const step = (this.pcvW - this.pcvPad * 2) / n;
      return this.pcvPad + step * i + step / 2;
    },
    pcvBreachPct(m) {
      const d = this.pnum(m.ComparableCount);
      return d > 0 ? (100 * (this.pnum(m.OverCount) + this.pnum(m.UnderCount))) / d : 0;
    },
    pcvMoverStyle(r) {
      const p = this.pnum(r.ChangePct);
      const w = Math.min(50, (Math.abs(p) / this.pcvMoverMax) * 50);
      return p >= 0 ? { left: "50%", width: w + "%" } : { right: "50%", width: w + "%" };
    },
    // ---- semantics ----
    // VISUAL PART_ID is CHAR and arrives space-padded — normalise both sides or nothing matches.
    pcvKey(s) { return String(s == null ? "" : (s.PartId != null ? s.PartId : s)).toUpperCase().replace(/\s+/g, " ").trim(); },
    pcvWatched(r) { return !!this.pcvWatchSet[this.pcvKey(r)]; },
    // "No basis" is its own state and must NEVER render like a flat month.
    pcvHasBasis(r) { return !!r && r.ChangePct != null && r.HasBasis !== false && this.pnum(r.HasBasis) !== 0; },
    pcvCls(r) {
      if (!this.pcvHasBasis(r)) return "pcv-nobasis";
      const p = this.pnum(r.ChangePct);
      return p > this.pcvTol ? "pcv-over" : p < -this.pcvTol ? "pcv-under" : "pcv-ok";
    },
    pcvChipCls(r) {
      if (!this.pcvHasBasis(r)) return "none";
      const p = this.pnum(r.ChangePct);
      return p > this.pcvTol ? "up" : p < -this.pcvTol ? "down" : "in";
    },
    pcvDotCls(r) { const c = this.pcvChipCls(r); return c === "up" ? "pcv-up" : c === "down" ? "pcv-dn" : "pcv-in"; },
    // The backend writes a ready-made caveat sentence (lot-size change, thin history, long gap),
    // so the UI never composes this prose and the two cannot drift apart.
    pcvCaveat(r) { return (r && r.QualityNote) ? String(r.QualityNote) : ""; },
    pcvLotShift(r) { return !!r && this.pnum(r.LotShift) === 1; },
    pcvThin(r) { return !!r && this.pnum(r.ThinSample) === 1; },
    pcvDrifting(r) { return !!r && this.pnum(r.SlowDrift) === 1; },
    /* ---- review signals ----
       The snapshot already computes every signal the cost team says it reviews on (see the 10 Aug
       Work Order Masters review: a trend BREAK matters more than a big number, and magnitude alone
       is not the signal). Until now SlowDrift and ThinSample were fetched on every row and never
       rendered anywhere — no column, no badge, no filter — so the one thing the glossary calls
       "worth a look even though nothing has been flagged" could not be listed. These two methods
       are the only place that mapping lives.

       Ordered most-worth-a-look first. The first three are reasons to LOOK; the last three are
       reasons the percentage may be MISLEADING, which is why they rank below the signals and read
       as caveats. Band already carries the monthly breach, so it is deliberately not repeated here.

       Label values must not be a substring of one another — InsTable's select filter matches with
       String.includes(), so "Thin" as a value would also select "Thin history". Same rule the Band
       strings already follow. */
    pcvSignals(r) {
      if (!r) return [];
      const out = [];
      if (this.pcvDrifting(r)) out.push("Quietly drifting");
      if (this.pnum(r.MonthsStale) >= 12) out.push("Stale cost");
      if (Math.abs(this.pnum(r.TrendRun)) >= 3) out.push("Trend run");
      if (this.pnum(r.MonthsSincePrior) > this.pcvMaxGap) out.push("Old baseline");
      if (this.pcvLotShift(r)) out.push("Lot size shift");
      if (this.pcvThin(r)) out.push("Thin history");
      return out;
    },
    /* 1 = look first. The queue's default sort reads this, and the ordering encodes a judgement
       worth stating: a caveat is an EXPLANATION, so a row carrying one ranks BELOW a clean
       unexplained move, not above it. A 40% jump with no lot-size change and three months of
       history is more worth someone's morning than the same jump on a part whose lot size tripled.

         1-3  a signal fired          — drifting / stale / trend run
         4    nothing flagged         — if this row is also outside the allowance it is a clean,
                                        unexplained breach, which is the most actionable kind
         5-7  only caveats fired      — the percentage is probably explainable; read the note

       Nothing is hidden at any rank. This is a lens on the same 2,000 rows, not a filter. */
    pcvSignalRank(r) {
      const s = this.pcvSignals(r);
      if (!s.length) return this.pcvTrueSignals + 1;
      const i = this.pcvSignalOrder.indexOf(s[0]);
      return i < this.pcvTrueSignals ? i + 1 : i + 2;
    },
    /* Which month ticks to draw. Every point at 8 or fewer, otherwise every other one plus the last,
       because 13 three-letter months across a 720-unit viewBox collide — and these charts stretch
       horizontally (preserveAspectRatio="none"), so the collision is worse on a wide screen.
       Until now every month and value on this tab lived only in a <title>, which meant the charts
       could not be read on a touch screen or on paper at all. */
    pcvMoTick(i, n) { return n <= 8 || i % 2 === 0 || i === n - 1; },
    pcvPrint() { window.print(); },
    // Signals read as something to act on; caveats read as muted, like the existing `lot` badge.
    pcvSigCls(f) { return this.pcvSignalOrder.indexOf(f) < this.pcvTrueSignals ? "sig" : "cav"; },
    /* Hover text per badge. Deliberately short and separate from QualityNote: that sentence explains
       ONE row's situation in prose, whereas this explains what the badge itself means, and a reader
       meeting "Trend run" for the first time should not have to open the glossary to find out. */
    pcvSigWhy(f) {
      const m = {
        "Quietly drifting": "Past the allowance over six months without any single month tripping it — invisible to the month-to-month test by design.",
        "Stale cost": "This part's newest cost activity is a year or more behind the newest activity in the data. The cost may simply be old.",
        "Trend run": "Three or more months moving the same direction. A run matters more than one spike.",
        "Old baseline": "Compared against a month more than " + this.pInt(this.pcvMaxGap) + " months earlier, so this is drift rather than a jump.",
        "Lot size shift": "Lot size changed materially between the two months, and cost per piece normally falls as lot size rises — so some of this is lot size, not price.",
        "Thin history": "Fewer than three months of cost history. The arithmetic is right but it is not a trend yet.",
      };
      return m[f] || "";
    },
    pcvTxnOutlier(r) { const a = this.pcvTxnAvg; return a > 0 && Math.abs(this.pnum(r.UnitCost) - a) / a > 0.25; },
    pcvBasisLabel(b) { return (b || this.pcvBasis) === "made" ? "made or bought" : "sold"; },
    // ---- formatters ----
    // pUsd rounds to whole dollars, which renders a $0.42 washer as "$0" and a 30% move as
    // "$0 -> $0". Unit costs and per-piece figures must use this instead.
    pcvUsd4(v) {
      if (v == null) return "—";
      const n = Number(v); if (isNaN(n)) return "—";
      const a = Math.abs(n), d = a >= 1 ? 2 : 4;
      return (n < 0 ? "-$" : "$") + a.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
    },
    pcvSignedPct(v) {
      if (v == null) return "—";
      const n = Number(v); if (isNaN(n)) return "—";
      // Values in the thousands of percent are real here (a part moving $0.01 -> $7.37M).
      // Abbreviate rather than printing fifteen digits into a table cell.
      if (Math.abs(n) >= 10000) return (n > 0 ? "+" : "-") + Math.round(Math.abs(n) / 1000) + "k%";
      return (n > 0 ? "+" : "") + n.toFixed(1) + "%";
    },
    pcvPeriod(v) {
      if (!v) return "—";
      const d = new Date(String(v).slice(0, 10) + "T00:00:00");
      return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    },
    pcvMoLabel(m) {
      const names = ["", "January", "February", "March", "April", "May", "June",
                     "July", "August", "September", "October", "November", "December"];
      return `${names[this.pnum(m.MonthNum)] || ""} ${m.Year}`;
    },
    pcvMoShort(m) {
      const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return names[this.pnum(m.MonthNum)] || "";
    },
    pcvPdShort(r) {
      const d = new Date(String(r.Period || "").slice(0, 10) + "T00:00:00");
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short" });
    },
    /* ---- drill chain -------------------------------------------------------------------------
       MI drills are INLINE cards, not modals, so all three levels can be on screen at once and
       nothing is destroyed by opening the next one. level 'parts' = the ranked list;
       level 'part' = one part's full history, with its postings loaded underneath on demand. */
    pcvOpenParts(scope, m) {
      const label = scope === "breach" ? `outside ±${this.pInt(this.pcvTol)}%`
                  : scope === "nobasis" ? "not comparable yet"
                  : scope === "month" ? `outside ±${this.pInt(this.pcvTol)}% — ${this.pcvMoLabel(m)}`
                  : "all comparable parts";
      const d = this.pcvDrill;
      d.open = true; d.level = "parts"; d.loading = true; d.q = "";
      d.scope = scope; d.period = m ? String(m.Period).slice(0, 10) : null;
      d.title = `Part cost variance (${this.pcvBasisLabel()}) — ${label}`;
      d.hint = "Click a part for its full cost history.";
      d.rows = []; d.part = null; d.head = null; d.months = []; d.txnPeriod = null; d.txn = [];
      api.partCostParts(this.pcvBasis, scope, d.period)
        .then(res => {
          d.rows = res && Array.isArray(res.rows) ? res.rows : [];
          d.truncated = this.pnum(res && res.truncated);
          d.loading = false;
        })
        .catch(() => { d.loading = false; });
    },
    pcvOpenPart(partId, opts) {
      const o = opts || {};
      const p = typeof partId === "string" ? partId : (partId && partId.PartId);
      if (!p) return;
      const d = this.pcvDrill;
      d.open = true; d.level = "part"; d.loading = true;
      d.part = p; d.head = null; d.months = []; d.txnPeriod = null; d.txn = []; d.retainedFrom = null;
      d.otherBasis = null; d.otherBasisMonths = 0;
      d.fromScope = o.from || null; d.fromPeriod = o.fromPeriod || null;
      d.title = `Part cost history — ${p}`;
      d.hint = "Click a month for the postings behind it.";
      api.partCostPart(this.pcvBasis, p)
        .then(res => {
          d.head = (res && res.head) || null;
          d.months = res && Array.isArray(res.months) ? res.months : [];
          d.otherBasis = (res && res.other_basis) || null;
          d.otherBasisMonths = this.pnum(res && res.other_basis_months);
          if (d.head && d.head.PartDescription) d.title = `Part cost history — ${p} · ${d.head.PartDescription}`;
          d.loading = false;
        })
        .catch(() => { d.loading = false; });
    },
    pcvBackToList() {
      const d = this.pcvDrill;
      this.pcvOpenParts(d.fromScope || "breach",
        d.fromPeriod ? { Period: d.fromPeriod, MonthNum: 0, Year: "" } : null);
    },
    // Same part, other basis — the reason a part that never ships is still reachable.
    pcvSwapBasisForPart() {
      const d = this.pcvDrill;
      const p = d.part, other = d.otherBasis;
      if (!p || !other) return;
      this.pcvSetBasis(other).then(() => this.pcvOpenPart(p));
    },
    // Clicking the same month again toggles the postings off.
    pcvOpenMonthTxn(row) {
      if (!row || !row.Period) return;
      const d = this.pcvDrill;
      const key = String(row.Period).slice(0, 10);
      if (d.txnPeriod === key) { d.txnPeriod = null; d.txn = []; return; }
      d.txnPeriod = key; d.txn = []; d.txnLoading = true;
      api.partCostTxn(this.pcvBasis, d.part, key)
        .then(res => {
          d.txn = res && Array.isArray(res.rows) ? res.rows : [];
          d.retainedFrom = (res && res.retained_from) || null;
          d.txnLoading = false;
        })
        .catch(() => { d.txnLoading = false; });
    },
    // Load Machining/Assembly P&L for the current operation toggle (+ latest month's statement).
    async loadProfitability() {
      this.profit = this.arr(await api.profitability(this.profitOp));
      const last = this.profit.length ? this.profit[this.profit.length - 1] : null;
      this.profitLatest = last;
      this.profitStmt = last ? await api.profitabilityStatement(this.profitOp, last.Year, last.MonthNum) : null;
    },
    setProfitOp(op) { if (this.profitOp === op) return; this.profitOp = op; this.loadProfitability(); },
    // Snapshots written before the Accounting-basis columns existed carry NULLs — fall back to the
    // gross figure so the tab keeps rendering instead of showing $0 for historical rows.
    wcFin(v, fallback) { return v == null ? fallback : v; },
    pnum(v) { return Number(v) || 0; },
    pabs(v) { return Math.abs(Number(v) || 0); },
    pUsd(v) { return v == null ? "—" : "$" + Math.round(Number(v)).toLocaleString(); },
    pUsd2(v) { return v == null ? "—" : "$" + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    pPct(v) { return v == null ? "—" : (Number(v) * 100).toFixed(1) + "%"; },
    pInt(v) { return v == null ? "—" : Math.round(Number(v)).toLocaleString(); },
    pnumfmt(v) { return v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 }); },
    pTurns(v) { return v == null ? "—" : (Number(v) || 0).toFixed(2) + "×"; },
    renderTab(id) {
      if (id === "machineshop") this.renderMs();
      else if (id === "shipping") this.renderShip();
      else if (id === "parts") this.renderParts();
      else if (id === "rmas") this.renderRmas();
      else if (id === "forecast") this.renderForecast();
    },
    // ---- overview (top KPI strip) ----
    async loadOverview() {
      try { this.ovCards = this.buildOverview(this.arr(await api.overview())); } catch (e) { /* */ }
    },
    buildOverview(cards) {
      // Trend coloring: green when the move is in the "good" direction for the metric.
      const cls = (hib, d) => d == null ? "muted" : ((hib ? d >= 0 : d <= 0) ? "good" : "bad");
      const arr = d => d == null ? "" : (d >= 0 ? "▲" : "▼");
      const pc = d => d == null ? "" : Math.abs(d * 100).toFixed(1) + "%";
      return cards.map(c => ({
        key: c.key, label: c.label, current_label: c.current_label,
        currentText: K.ovFmt(c.unit, c.current),
        secondaryText: c.secondary != null ? K.ovFmt(c.unit, c.secondary) : null,
        secondaryLabel: c.secondary_label,
        same_month_label: c.same_month_label,
        sameText: K.ovFmt(c.unit, c.same_month),
        smDelta: c.same_month_delta_pct, smCls: cls(c.higher_is_better, c.same_month_delta_pct),
        smArrow: arr(c.same_month_delta_pct), smPct: pc(c.same_month_delta_pct),
        ytdText: K.ovFmt(c.unit, c.ytd),
        priorText: K.ovFmt(c.unit, c.prior_ytd),
        ytdDelta: c.delta_pct, ytdCls: cls(c.higher_is_better, c.delta_pct),
        ytdArrow: arr(c.delta_pct), ytdPct: pc(c.delta_pct),
      }));
    },
    // ---- charts ----
    renderMs() {
      const el = this.$refs.msMount; if (!el) return;
      const ms = this.tail(this._ms);
      if (!ms.length) { el.innerHTML = '<div class="hint">Awaiting first Machine Shop sync.</div>'; return; }
      const usdRound = v => "$" + Math.round(v);
      K.barChart(el, {
        cats: ms.map(r => K.M3(r.MonthNum)),
        series: [{ key: "dph", color: K.C.accent, data: ms.map(r => r.DollarsPerHour || 0) }],
        goals: ms.map(r => r.GoalDollarsPerHour || 0), colorByGoal: true, goalDir: "high",
        valfmt: usdRound, yfmt: usdRound,
        onBar: i => this.drillMachineMonth(ms[i]),
      });
    },
    setShipBasis(v) { this.shipBasis = v; this.$nextTick(() => this.renderShipOnTime()); },
    renderShip() {
      this.renderShipOnTime();
      this.renderShipGap();
      this.renderShipSpeed();
    },
    // On-time overlay: Original Promise vs Revised. Values from ShipIntegrityMonthly are 0-100.
    // Click a month to drill into that month's orders.
    renderShipOnTime() {
      const el = this.$refs.shipMount; if (!el) return;
      const s = this.tail(this._shipInt || []);
      if (!s.length) { el.innerHTML = '<div class="hint">Awaiting first Shipping sync.</div>'; return; }
      const cats = s.map(r => K.M3(r.MonthNum));
      const revised = s.map(r => (r.OnTimeSystemPct != null ? Number(r.OnTimeSystemPct) : null));
      const promise = s.map(r => (r.OnTimeRealPct != null ? Number(r.OnTimeRealPct) : null));
      const yfmt = v => v.toFixed(0) + "%";
      const onPoint = i => this.openShipMonth(s[i]);
      if (this.shipBasis === "revised") {
        K.lineChart(el, { cats, data: revised, goal: 95, yfmt, onPoint });
      } else if (this.shipBasis === "promise") {
        K.lineChart(el, { cats, data: promise, goal: 95, yfmt, onPoint });
      } else {
        K.multiLineChart(el, {
          cats, goal: 95, yfmt, onPoint,
          series: [
            { key: "On-time to Original Promise", color: K.C.molten, data: promise },
            { key: "On-time to Revised Date", color: K.C.accent, data: revised },
          ],
        });
      }
    },
    // Hidden-lateness trend: on-time vs adjusted date but late vs the original promise.
    renderShipGap() {
      const el = this.$refs.shipGapMount; if (!el) return;
      const s = this.tail(this._shipInt || []);
      if (!s.length) { el.innerHTML = '<div class="hint">Awaiting first Shipping sync.</div>'; return; }
      K.lineChart(el, {
        cats: s.map(r => K.M3(r.MonthNum)),
        data: s.map(r => (r.HiddenLatePct != null ? Number(r.HiddenLatePct) : null)),
        yfmt: v => v.toFixed(0) + "%",
      });
    },
    // 100%-stacked speed mix (normalized so each month's buckets sum to exactly 100).
    renderShipSpeed() {
      const el = this.$refs.shipSpeedMount; if (!el) return;
      const s = this.tail(this._shipInt || []);
      if (!s.length) { el.innerHTML = '<div class="hint">Awaiting first Shipping sync.</div>'; return; }
      const norm = r => {
        const p = [Number(r.PctNextDay) || 0, Number(r.PctTwoToThree) || 0,
                   Number(r.PctFourToFourteen) || 0, Number(r.PctOverFourteen) || 0];
        const t = p.reduce((a, b) => a + b, 0) || 1;
        return p.map(v => v * 100 / t);
      };
      const N = s.map(norm);
      K.barChart(el, {
        cats: s.map(r => K.M3(r.MonthNum)),
        series: [
          { key: "nextday", color: K.C.good, data: N.map(p => p[0]) },
          { key: "twothree", color: K.C.accent, data: N.map(p => p[1]) },
          { key: "fourfourteen", color: K.C.warn, data: N.map(p => p[2]) },
          { key: "overfourteen", color: K.C.bad, data: N.map(p => p[3]) },
        ],
        valOnTop: false, yfmt: v => v.toFixed(0) + "%",
        onBar: i => this.openShipMonth(s[i]),
      });
    },
    // ---- shipping drilldowns ----
    async openShipMonth(row) {
      if (!row) return;
      const d = this.shipDrill;
      d.kind = "orders";
      d.title = `Orders shipped — ${K.M3(row.MonthNum)} ${row.Year}`;
      d.hint = "Each order's Original Promise vs Revised vs Actual ship date. Red = late vs the original promise; amber = the date was changed to land on the ship date.";
      d.q = ""; d.rep = ""; d.via = ""; d.flag = ""; d.bucket = ""; d.changedOnly = false;
      d.rows = []; d.open = true;
      try { d.rows = this.arr(await api.shippingDetail(row.Year, row.MonthNum)); } catch (e) { d.rows = []; }
    },
    onDeptClick(e) {
      const tr = e.target && e.target.closest ? e.target.closest("tr[data-dept]") : null;
      if (tr) this.openDeptTrend(tr.getAttribute("data-dept"));
    },
    openDeptTrend(dept) {
      if (!dept) return;
      this.shipDrill.kind = "dept";
      this.shipDrill.title = `Late shipments — ${K.prettyDept(dept)}`;
      this.shipDrill.hint = "Monthly late-order count for this department (spreadsheet-sourced; no per-order link exists).";
      this.shipDrill.open = true;
      this.$nextTick(() => {
        const el = this.$refs.shipDeptMount; if (!el) return;
        const rows = (this._shipLateMonthly || []).filter(r => r.Department === dept);
        if (!rows.length) { el.innerHTML = '<div class="hint">No monthly data for this department.</div>'; return; }
        K.lineChart(el, {
          cats: rows.map(r => K.M3(r.MonthNum)),
          data: rows.map(r => Number(r.LateOrders) || 0),
          yfmt: v => Math.round(v).toString(),
        });
      });
    },
    renderParts() {
      const el = this.$refs.partsMount; if (!el) return;
      const p = this.tail(this._parts);
      if (!p.length) { el.innerHTML = '<div class="hint">Awaiting first Parts sync.</div>'; return; }
      K.barChart(el, {
        cats: p.map(r => K.M3(r.MonthNum)),
        series: [{ key: "parts", color: K.C.duct, data: p.map(r => r.InvoicedPartsNet || 0) }],
        valfmt: K.money, yfmt: K.money,
        onBar: i => this.drillPartsMonth(p[i]),
      });
    },
    renderRmas() {
      const el = this.$refs.rmaMount; if (!el) return;
      const rm = this.tail(this._rmas);
      if (!rm.length) { el.innerHTML = '<div class="hint">Awaiting first RMA sync.</div>'; return; }
      const whole = v => Math.round(v).toString();
      K.barChart(el, {
        cats: rm.map(r => K.M3(r.MonthNum)),
        series: [{ key: "rma", color: K.C.molten, data: rm.map(r => r.RmaCount || 0) }],
        valfmt: whole, yfmt: whole,
        onBar: i => this.drillRmaMonth(rm[i]),
      });
      const last = rm[rm.length - 1];
      this.rmaNote = last
        ? `${K.M3(last.MonthNum)} ${last.Year}: ${last.RmaCount || 0} RMAs · ${last.WarrantyCount || 0} warranty · ` +
          `${last.RepairWoCount || 0} created a repair WO · ${last.OpenCount || 0} open / ${last.ClosedCount || 0} closed.`
        : "";
    },
    renderForecast() {
      this.forecastCards.forEach((c, i) => {
        const holder = this.$refs["fc" + i];
        const el = Array.isArray(holder) ? holder[0] : holder;
        if (el && c.forecast && c.forecast.length) {
          K.forecastChart(el, { history: c.history, forecast: c.forecast, unit: c.unit, goal: c.goal });
        }
      });
    },
    // ---- drill-downs ----
    openDrill(title, hint, kind) {
      this.drill = { open: true, title, hint, kind, html: kind === "table" ? '<div class="hint">loading…</div>' : "" };
    },
    async drillMachineMonth(row) {
      if (!row) return;
      const label = (row.MonthName || K.M3(row.MonthNum)) + " " + row.Year;
      this.openDrill("Machine Shop $/Hr (weekly) — " + label,
        "receipts $ per machinist hour, by week ending · dashed line = goal", "chart");
      await this.$nextTick();
      const el = this.$refs.drillMount; if (el) el.innerHTML = '<div class="hint">loading…</div>';
      try {
        const r = await api.machineshopWeekly(row.Year, row.MonthNum);
        const pts = (r && r.points) || [];
        if (!pts.length) el.innerHTML = '<div class="hint">No weekly detail available for this month.</div>';
        else K.lineChart(el, { cats: pts.map(p => p.wk.slice(5)), data: pts.map(p => p.dph), goal: r.goal, yfmt: v => "$" + Math.round(v) });
      } catch (e) { if (el) el.innerHTML = '<div class="hint">Failed to load weekly detail.</div>'; }
    },
    // Month bar → dedicated Parts drill. Loads the full invoice list; part-line detail is
    // lazy-loaded when the user first switches to the "Parts (lines)" grain.
    async drillPartsMonth(row) {
      if (!row) return;
      const d = this.partsDrill;
      d.title = "Parts Sales — " + ((row.MonthName || K.M3(row.MonthNum)) + " " + row.Year);
      d.year = row.Year; d.month = row.MonthNum; d.mode = "invoices";
      d.q = ""; d.cust = ""; d.type = ""; d.family = "";
      d.inv = []; d.invTotal = 0; d.invCount = 0; d.lines = []; d.linesLoaded = false; d.open = true;
      try {
        const p = await api.partsInvoices(row.Year, row.MonthNum);
        d.inv = this.arr(p && p.invoices);
        d.invTotal = (p && p.total) || 0;
        d.invCount = (p && p.invoice_count) || d.inv.length;
      } catch (e) { d.inv = []; }
    },
    async loadPartsLines() {
      const d = this.partsDrill;
      try { const r = await api.partsLines(d.year, d.month); d.lines = this.arr(r && r.lines); }
      catch (e) { d.lines = []; }
      d.linesLoaded = true;
    },
    setPartsMode(m) {
      const d = this.partsDrill;
      if (d.mode === m) return;
      d.mode = m; d.q = ""; d.cust = ""; d.type = ""; d.family = "";
      if (m === "lines" && !d.linesLoaded) this.loadPartsLines();
    },
    // Invoice TYPE code → friendly label (VMFG: I = invoice, M = credit memo).
    typeLabel(t) { return t === "M" ? "Credit Memo" : (t === "I" ? "Invoice" : (t || "")); },
    // Currency for template use (K is a module import, not visible in templates).
    usd(v) { return K.usd0(Number(v) || 0); },
    // Download the currently-filtered Parts rows as a real .xlsx (amounts/qty as numbers).
    exportParts() {
      const d = this.partsDrill, rows = this.partsFiltered;
      if (!rows.length || typeof document === "undefined") return;
      let cols, aoa;
      if (d.mode === "lines") {
        cols = ["Invoice", "Date", "Customer", "Type", "Line", "Part", "Description", "Family", "Product Code", "Qty", "Amount"];
        aoa = [cols].concat(rows.map(r => [
          r.invoice, r.date, r.customer, this.typeLabel(r.type), r.lineNum, r.part, r.partDesc,
          (r.family == null || r.family === "") ? "(unclassified)" : r.family, r.productCode,
          Number(r.qty) || 0, Number(r.amount) || 0]));
      } else {
        cols = ["Invoice", "Date", "Customer", "Type", "Amount"];
        aoa = [cols].concat(rows.map(r => [r.invoice, r.date, r.customer, this.typeLabel(r.type), Number(r.amount) || 0]));
      }
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = cols.map((c, ci) => {
        let w = String(c || "").length;
        aoa.slice(1).forEach(r => { const l = String(r[ci] == null ? "" : r[ci]).length; if (l > w) w = l; });
        return { wch: Math.min(50, Math.max(10, w + 2)) };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, d.mode === "lines" ? "Parts" : "Invoices");
      const stem = (d.title || "parts").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "parts";
      XLSX.writeFile(wb, `${stem}-${d.mode}.xlsx`);
    },
    // Month bar → dedicated filterable RMA drill (dept / reason / type / status + search).
    async drillRmaMonth(row) {
      if (!row) return;
      const label = (row.MonthName || K.M3(row.MonthNum)) + " " + row.Year;
      const d = this.rmaDrill;
      d.title = "Returned Parts — " + label;
      d.hint = "RMA lines created this month · part, qty, responsible dept, reason & status. Filter by dept / reason / type / status, or search RMA / customer / part.";
      d.q = ""; d.dept = ""; d.reason = ""; d.type = ""; d.status = "";
      d.rows = []; d.open = true;
      try { const res = await api.rmaDetail(row.Year, row.MonthNum); d.rows = this.arr(res && res.rmas); }
      catch (e) { d.rows = []; }
    },
    // ---- provenance popover ----
    showPop(key, ev) {
      const m = this.prov && this.prov.metrics ? this.prov.metrics.find(x => x.key === key) : null;
      if (!m) return;
      this.pop.html = K.provPopoverHtml(this.prov, m);
      this.pop.open = true;
      const r = ev.currentTarget.getBoundingClientRect();
      const pw = Math.min(340, window.innerWidth - 20);
      let left = Math.min(r.left, window.innerWidth - pw - 10); if (left < 10) left = 10;
      let top = r.bottom + 6;
      this.pop.style = { left: left + "px", top: top + "px", maxWidth: pw + "px" };
      this.$nextTick(() => {
        const popEl = this.$el.querySelector(".mi-prov-pop");
        if (!popEl) return;
        const ph = popEl.getBoundingClientRect().height;
        if (top + ph > window.innerHeight - 10) {
          top = Math.max(10, r.top - ph - 6);
          this.pop.style = Object.assign({}, this.pop.style, { top: top + "px" });
        }
      });
    },
    hidePop() { this.pop.open = false; this.pop.html = ""; },
    onPopClick(e) { if (e.target.classList && e.target.classList.contains("pop-close")) this.hidePop(); },
  },
};
</script>

<!-- NOT scoped on purpose: the SVG chart nodes are created imperatively (outside the
     Vue template), so Vue's scoped data-attribute would miss them. Everything is instead
     namespaced under .mi-wrap so nothing leaks into the rest of the portal. -->
<style>
/* Light, portal-branded skin — matches Foundry Insights (fi-*) so the two reports read
   as one family: white jcs-portal-card, JC Steele red accent, light KPI tiles, light charts.
   Same selectors as the original dark theme; only the values changed (vars drive the rest). */
.mi-wrap {
  --mi-accent:#9e2436; /* JC Steele brand red — same as Foundry's --fi-accent */
  --bg:#fff; --panel:#fff; --panel2:#f1f5f9; --line:#e4e9ee;
  --text:#2a2a2a; --muted:#7a828b; --accent:#2f6db0; --molten:#c0603a;
  --good:#2f7a4f; --bad:#b3402f; --warn:#c98a2b;
  margin:4px 16px 0; background:#fff; color:var(--text);
  border:1px solid var(--line); border-top:3px solid var(--mi-accent);
  border-radius:8px; box-shadow:0 1px 3px rgba(0,0,0,0.06);
  font-size:14px; padding:22px 22px 28px;
}
.mi-wrap *{box-sizing:border-box}
.mi-head{text-align:center;margin-bottom:16px}
.mi-title{font-size:24px;font-weight:800;color:var(--text);margin:0 0 6px}
.mi-sub{font-size:13px;color:#5a636c;max-width:680px;margin:0 auto 10px}
.mi-fresh{display:flex;align-items:center;flex-wrap:wrap;justify-content:center;gap:10px;font-size:12px;margin-bottom:2px}
/* Freshness pill — mirrors Foundry Insights' fi-fresh (weekend-aware level colors + meta). */
.mi-wrap .mi-pill{display:inline-flex;align-items:center;flex-wrap:wrap;gap:8px;font-size:12px;padding:4px 12px;border-radius:999px;background:var(--panel2);color:#46525e}
.mi-wrap .mi-pill-dot{width:9px;height:9px;border-radius:50%;background:#3d9a5a}
.mi-wrap .mi-pill.lvl-warning .mi-pill-dot{background:#d9a441}
.mi-wrap .mi-pill.lvl-lagging .mi-pill-dot{background:#e0842f}
.mi-wrap .mi-pill.lvl-alert .mi-pill-dot{background:#cc4a3f}
.mi-wrap .mi-pill-meta{color:var(--muted);padding-left:10px;border-left:1px solid var(--line)}
/* Chart range filter (segmented control). */
.mi-wrap .mi-filters{display:flex;align-items:center;gap:10px;justify-content:center;margin:0 0 16px}
.mi-wrap .mi-filters-lbl{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.03em;font-weight:700}
.mi-wrap .mi-seg{display:inline-flex;border:1px solid var(--line);border-radius:8px;overflow:hidden}
.mi-wrap .mi-seg button{border:none;background:var(--panel);color:var(--muted);font-size:12px;font-weight:600;padding:5px 13px;cursor:pointer;border-right:1px solid var(--line)}
.mi-wrap .mi-seg button:last-child{border-right:none}
.mi-wrap .mi-seg button.on{background:var(--mi-accent);color:#fff}
.mi-wrap .mi-seg button:hover:not(.on){background:var(--panel2)}
/* Shipping: per-card on-time basis toggle + hidden-lateness stat + speed-mix legend. */
.mi-wrap .card-head-row{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap}
.mi-wrap .mi-seg.ship-basis{margin-top:1px}
.mi-wrap .ship-stat{display:flex;align-items:baseline;gap:10px;margin:2px 0 10px}
.mi-wrap .ship-stat-val{font-size:30px;font-weight:800;color:#b3402f;line-height:1}
.mi-wrap .ship-stat-sub{font-size:12px;color:var(--muted)}
.mi-wrap .ship-legend{display:inline-flex;align-items:center;gap:4px;margin-left:12px;font-size:11px;color:var(--muted)}
.mi-wrap .ship-legend .sw{display:inline-block;width:10px;height:10px;border-radius:2px}
.mi-wrap .ship-dept .clickrow{cursor:pointer}
.mi-wrap .ship-dept .clickrow:hover td{background:var(--panel2)}
.mi-wrap .ship-filters{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:0 0 12px}
.mi-wrap .ship-filters input[type=text]{padding:5px 9px;border:1px solid var(--line);border-radius:6px;font-size:12px;min-width:180px}
.mi-wrap .ship-filters select{padding:5px 8px;border:1px solid var(--line);border-radius:6px;font-size:12px;background:var(--panel)}
.mi-wrap .ship-filters .ship-chk{font-size:12px;color:var(--muted);display:inline-flex;align-items:center;gap:5px}
.mi-wrap .ship-filters .ship-count{font-size:11px;color:var(--muted);margin-left:auto}
.mi-wrap .parts-drill-toolbar{margin:0 0 10px}
.mi-wrap .ship-filters .parts-export{padding:5px 11px;border:1px solid var(--line);border-radius:6px;font-size:12px;font-weight:600;background:var(--panel);color:var(--text);cursor:pointer}
.mi-wrap .ship-filters .parts-export:hover:not(:disabled){background:var(--panel2)}
.mi-wrap .ship-filters .parts-export:disabled{opacity:.5;cursor:default}
.mi-wrap table.ship-detail{font-size:12px;white-space:nowrap}
.mi-wrap table.ship-detail td.cust{white-space:normal}
.mi-wrap table.ship-detail tr.late td{background:#fbecea}
.mi-wrap table.ship-detail tr.matched td{background:#fdf3e0}
/* Same drill row tints for the shared InsTable rows (late/matched shipping, credit-memo parts). */
.mi-wrap .ins-table tr.late td{background:#fbecea}
.mi-wrap .ins-table tr.matched td{background:#fdf3e0}
.mi-wrap .ins-table tr.credit td.num{color:var(--warn)}
.mi-wrap .badge{display:inline-block;padding:1px 8px;border-radius:999px;font-size:11px;font-weight:700}
.mi-wrap .badge.live{color:var(--good);background:#e4f3ea}
.mi-wrap .badge.demo{color:#a5631b;background:#fdecd6}
.mi-wrap .grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.mi-wrap .card{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:16px}
.mi-wrap .card.wide{grid-column:1 / -1}
.mi-wrap .card h2{font-size:15px;margin:0 0 2px;font-weight:700;color:var(--text)}
.mi-wrap .hint{color:var(--muted);font-size:12px;margin:0 0 10px}
.mi-wrap svg{display:block;width:100%;height:auto;overflow:visible}
.mi-wrap .axis{stroke:var(--line);stroke-width:1}
.mi-wrap .grid-line{stroke:var(--line);stroke-dasharray:2 3;opacity:.5}
.mi-wrap .tick{fill:var(--muted);font-size:10px}
.mi-wrap .barlabel{fill:var(--text);font-size:10px;font-variant-numeric:tabular-nums;paint-order:stroke;stroke:var(--bg);stroke-width:3px;stroke-linejoin:round}
.mi-wrap .linelabel{fill:var(--accent);font-size:10px;font-weight:600;font-variant-numeric:tabular-nums;paint-order:stroke;stroke:var(--bg);stroke-width:3.5px;stroke-linejoin:round}
.mi-wrap .chartscroll{overflow-x:auto}
.mi-wrap .card.wide.drill{border-color:var(--mi-accent);border-top-width:3px}
.mi-wrap .drill-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
.mi-wrap .drill-close{background:transparent;border:1px solid var(--line);color:var(--muted);border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px}
.mi-wrap .drill-close:hover{color:var(--text);border-color:var(--mi-accent)}
.mi-wrap .drill-table{width:100%;border-collapse:collapse;font-size:12px;font-variant-numeric:tabular-nums}
.mi-wrap .drill-table th,.mi-wrap .drill-table td{padding:5px 10px;text-align:left;border-bottom:1px solid var(--line);white-space:nowrap}
.mi-wrap .drill-table th{color:var(--muted);font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:.5px}
.mi-wrap .drill-table td.num,.mi-wrap .drill-table th.num{text-align:right}
.mi-wrap .drill-table td.cust{white-space:normal;min-width:180px}
.mi-wrap .drill-table tbody tr:hover{background:var(--panel2)}
.mi-wrap .drill-table .rank{color:var(--muted);width:26px}
.mi-wrap .drill-table .others td{color:var(--muted);font-style:italic}
.mi-wrap .drill-table .credit td.num{color:var(--warn)}
.mi-wrap .drill-table tfoot td{border-top:2px solid var(--line);border-bottom:none;font-weight:700;padding-top:8px}
.mi-wrap .info-btn{display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;margin-left:7px;border:1px solid var(--line);border-radius:50%;background:transparent;color:var(--muted);font-size:11px;line-height:1;cursor:pointer;vertical-align:middle;padding:0;font-family:inherit}
.mi-wrap .info-btn:hover{color:var(--mi-accent);border-color:var(--mi-accent)}
.mi-wrap .auth{display:inline-block;padding:1px 7px;border-radius:9px;font-size:10px;font-weight:700;letter-spacing:.4px;border:1px solid currentColor;white-space:nowrap}
.mi-wrap .auth.AUTHORITATIVE{color:var(--good)}
.mi-wrap .auth.INFORMATIONAL{color:var(--muted)}
.mi-wrap .auth.RECONCILED{color:var(--accent)}
.mi-wrap .sys{display:inline-block;padding:1px 7px;border-radius:5px;font-size:10px;font-weight:600;background:var(--panel2);border:1px solid var(--line);color:var(--text);white-space:nowrap}
.mi-wrap .lineage-table{width:100%;border-collapse:collapse;font-size:12px}
.mi-wrap .lineage-table th,.mi-wrap .lineage-table td{padding:7px 10px;text-align:left;border-bottom:1px solid var(--line);vertical-align:top}
.mi-wrap .lineage-table th{color:var(--muted);font-weight:600;text-transform:uppercase;font-size:10px;letter-spacing:.5px;white-space:nowrap}
.mi-wrap .lineage-table .metric{font-weight:600;min-width:120px}
.mi-wrap .lineage-table .basis{color:var(--muted);min-width:240px;line-height:1.45}
.mi-wrap .lineage-table .src{max-width:300px}
.mi-wrap .lineage-table .src code{font-size:11px;color:var(--text);background:var(--panel2);padding:1px 5px;border-radius:4px;white-space:normal;overflow-wrap:anywhere}
.mi-wrap .lineage-table .asof{white-space:nowrap;color:var(--muted);font-size:11px}
.mi-wrap .lineage-file{display:block;margin-top:4px;color:var(--muted);font-size:11px}
.mi-wrap .lineage-src-link{display:block;margin-top:4px;color:var(--mi-accent);font-size:11px;font-weight:600;text-decoration:none}
.mi-wrap .lineage-src-link:hover{text-decoration:underline}
/* Data-source explainer cards — mirror Foundry Insights' fi-src-* cards. */
.mi-wrap .mi-src-cards{display:flex;flex-wrap:wrap;gap:12px;margin:4px 0 18px}
.mi-wrap .mi-src-card{flex:1 1 240px;display:flex;gap:10px;padding:12px 14px;border:1px solid var(--line);border-radius:8px;background:var(--panel2)}
.mi-wrap .mi-src-ic{font-size:20px;line-height:1.2;flex:0 0 auto}
.mi-wrap .mi-src-name{font-weight:700;color:var(--text);font-size:13px;margin-bottom:3px}
.mi-wrap .mi-src-desc{font-size:12px;color:var(--muted);line-height:1.45}
.mi-wrap .mi-src-links{display:flex;flex-wrap:wrap;gap:14px;margin-top:7px}
.mi-wrap .mi-src-link{font-size:12px;font-weight:600;color:var(--mi-accent);text-decoration:none}
.mi-wrap .mi-src-link:hover{text-decoration:underline}
.mi-wrap .mi-src-feeds{font-size:11px;color:var(--muted);margin-top:6px;text-transform:uppercase;letter-spacing:.03em;font-weight:700}
.mi-wrap .mi-prov-foot{font-size:11px;color:var(--muted);margin:10px 2px 0}
.mi-wrap .mi-prov-help{font-size:12px;color:var(--muted);margin:12px 2px 0;padding-top:10px;border-top:1px solid var(--line)}
.mi-wrap .mi-prov-help a{color:var(--mi-accent);font-weight:600}
.mi-prov-pop{position:fixed;z-index:6000;max-width:340px;background:#fff;border:1px solid #e4e9ee;border-top:3px solid #9e2436;border-radius:8px;padding:12px 14px;box-shadow:0 10px 32px rgba(0,0,0,.22);font-size:12px;line-height:1.5;color:#2a2a2a}
.mi-prov-pop h3{margin:0 0 6px;font-size:13px;font-weight:700;display:flex;align-items:center;gap:8px;flex-wrap:wrap;color:#2a2a2a}
.mi-prov-pop .row{margin-top:6px;color:#7a828b}
.mi-prov-pop .row b{color:#2a2a2a;font-weight:600}
.mi-prov-pop code{font-size:11px;background:#f1f5f9;padding:1px 5px;border-radius:4px;color:#46525e}
.mi-prov-pop .basis{margin-top:8px;color:#46525e}
.mi-prov-pop .auth{display:inline-block;padding:1px 7px;border-radius:9px;font-size:10px;font-weight:700;border:1px solid currentColor}
.mi-prov-pop .auth.AUTHORITATIVE{color:#2f7a4f}.mi-prov-pop .auth.RECONCILED{color:#2f6db0}
.mi-prov-pop .sys{display:inline-block;padding:1px 7px;border-radius:5px;font-size:10px;background:#f1f5f9;border:1px solid #e4e9ee;color:#46525e}
.mi-prov-pop .pop-close{position:absolute;top:6px;right:9px;background:none;border:none;color:#8a929b;cursor:pointer;font-size:15px;line-height:1;padding:0}
.mi-prov-pop .pop-close:hover{color:#2a2a2a}
.mi-wrap .scrap-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:16px}
.mi-wrap .scrap-col h3{font-size:12px;margin:0 0 6px;font-weight:620;color:var(--text)}
.mi-wrap .scrap-mini{font-size:11.5px}
.mi-wrap .scrap-mini th,.mi-wrap .scrap-mini td{padding:4px 8px}
.mi-wrap .scrap-mini td.cust,.mi-wrap .scrap-mini .cust{white-space:normal;color:var(--muted);font-size:10.5px;margin-top:1px}
.mi-wrap .mi-tabs{display:flex;gap:2px;flex-wrap:wrap;justify-content:center;margin:0 0 18px;border-bottom:1px solid var(--line)}
.mi-wrap .mi-tabs a{padding:9px 15px;color:var(--muted);text-decoration:none;font-size:13px;font-weight:600;border-bottom:2px solid transparent;margin-bottom:-1px}
.mi-wrap .mi-tabs a:hover{color:var(--text)}
.mi-wrap .mi-tabs a.active{color:var(--mi-accent);border-bottom-color:var(--mi-accent)}
/* Top KPI strip — mirrors Foundry Insights' fi-kpi cards (current + same-month + YTD + prior-YTD). */
.mi-wrap .mi-kpis{display:flex;flex-wrap:wrap;gap:14px;justify-content:center;margin-bottom:20px}
.mi-wrap .mi-kpi{flex:1 1 200px;max-width:260px;border:1px solid var(--line);border-left:4px solid var(--mi-accent);border-radius:10px;padding:14px 16px;background:var(--panel)}
.mi-wrap .mi-kpi-head{display:flex;align-items:center;gap:6px}
.mi-wrap .mi-kpi-label{color:var(--muted);font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.03em}
.mi-wrap .mi-kpi-value{font-size:26px;font-weight:800;color:var(--text);margin-top:4px;font-variant-numeric:tabular-nums}
.mi-wrap .mi-kpi-when{font-size:11px;color:var(--muted);margin-top:1px}
.mi-wrap .mi-kpi-secondary{font-size:14px;font-weight:600;color:var(--text);margin-top:6px}
.mi-wrap .mi-kpi-secondary b{font-size:19px;font-weight:800;color:var(--molten,#c0603a);font-variant-numeric:tabular-nums}
.mi-wrap .mi-kpi-cmp{margin-top:10px;border-top:1px solid var(--line);padding-top:8px;display:flex;flex-direction:column;gap:4px}
.mi-wrap .mi-kpi-cmp-row{display:flex;align-items:baseline;gap:6px;font-size:12px}
.mi-wrap .mi-kpi-cmp-lbl{color:var(--muted);flex:0 0 80px;text-transform:uppercase;font-size:10px;letter-spacing:.03em;font-weight:700}
.mi-wrap .mi-kpi-cmp-val{color:var(--text);font-weight:600;font-variant-numeric:tabular-nums}
.mi-wrap .mi-kpi-delta{margin-left:auto;font-size:11px;font-weight:700;padding:0 6px;border-radius:999px;white-space:nowrap}
.mi-wrap .mi-kpi-delta-ar{margin-right:2px}
.mi-wrap .mi-kpi-delta.good{color:#2f7a4f;background:#e4f3ea}
.mi-wrap .mi-kpi-delta.bad{color:#b3402f;background:#fbe6e3}
.mi-wrap .mi-kpi-delta.muted{color:var(--muted);background:var(--panel2)}
html.dark-mode .mi-wrap .mi-kpi-delta.good{background:rgba(47,122,79,.28);color:#8fd6a8}
html.dark-mode .mi-wrap .mi-kpi-delta.bad{background:rgba(179,64,47,.28);color:#f0a99e}
@media (max-width:575px){.mi-wrap .mi-kpi{flex:1 1 100%;max-width:100%}}
.mi-wrap .mi-noaccess{background:var(--panel);border:1px solid var(--line);border-radius:10px;padding:24px}
@media (max-width:820px){.mi-wrap .grid{grid-template-columns:1fr}}
@media (max-width:575px){.mi-wrap{padding:16px 12px 22px;margin:4px 8px 0}}
/* Portal dark mode — mirror Foundry's html.dark-mode treatment by re-pointing the vars. */
/* ===== Part Cost Variance =====================================================================
   Expressed in MI's own CSS variables wherever possible, so the html.dark-mode .mi-wrap block
   below (which swaps those variables) carries most of dark mode for free. Only the semantic
   red/green/amber need explicit dark overrides.
   The ±10% allowance is drawn as an OBJECT (a shaded band) rather than described in prose, and
   marks carry BOTH colour and shape — circle inside, triangle outside. This tab's whole message
   is red-vs-green, unlike the rest of MI where colour is decoration, so that redundancy is what
   keeps it readable on a mono printer and for red-green colour blindness. */
.mi-wrap .pcv-band{fill:#2f7a4f;fill-opacity:.07}
.mi-wrap .pcv-band-edge{stroke:#7fae93;stroke-width:1;stroke-dasharray:3 3}
.mi-wrap .pcv-band-lbl{font-size:9px;fill:#6a8f7a}
/* Axis tick labels. Small and muted: they orient the reader without competing with the marks. */
.mi-wrap .pcv-axlbl{font-size:9px;fill:var(--muted)}
/* The cost-trust caveat. Left rule rather than a colour wash: it is a standing property of the data,
   not an alert, and it must not read as though something is currently wrong. */
.mi-wrap .pcv-trust{border-left:3px solid var(--border);padding:8px 0 8px 11px}
/* --- trend lines. Colour identifies WHICH part here; direction is read off the band, so these
   deliberately do not reuse the red/green pair that means up/down everywhere else on this tab. --- */
.mi-wrap .pcv-line{fill:none;stroke-width:1.9;stroke-linejoin:round;stroke-linecap:round}
.mi-wrap .pcv-line-watch{stroke-width:2.8}
.mi-wrap .pcv-line-g{cursor:pointer}
.mi-wrap .pcv-line-g:hover .pcv-line{stroke-width:3.2}
/* hovering one line fades the rest rather than hiding them, so a crossing stays readable */
.mi-wrap .pcv-line-mute{opacity:.16}
.mi-wrap .pcv-lpt{stroke:var(--panel);stroke-width:.8}
/* a point pinned to the top or bottom of the axis — bigger, so a clamped plateau is not misread
   as the cost levelling off. The real number stays in the tooltip. */
.mi-wrap .pcv-lpt-clamp{stroke-width:1.6;stroke-dasharray:1.5 1.5}
.mi-wrap .pcv-line-lbl{font-size:9px;font-weight:600}
.mi-wrap .pcv-leg-mute{opacity:.35}
.mi-wrap .pcv-up-t{color:#b3402f}
.mi-wrap .pcv-dn-t{color:#2f7a4f}
.mi-wrap .pcv-mark{cursor:pointer}
.mi-wrap .pcv-in{fill:#9aa3ac}
.mi-wrap .pcv-up{fill:#b3402f}
.mi-wrap .pcv-dn{fill:#2f7a4f}
/* hollow = carries a caveat (lot-size change, thin history, long gap), so only SOLID marks
   outside the band read as clean breaches at a glance */
.mi-wrap .pcv-soft{fill:var(--panel);stroke-width:1.4}
.mi-wrap .pcv-soft.pcv-up{stroke:#b3402f}
.mi-wrap .pcv-soft.pcv-dn{stroke:#2f7a4f}
.mi-wrap .pcv-soft.pcv-in{stroke:#9aa3ac}
.mi-wrap .pcv-watch{stroke:var(--text);stroke-width:1.2}
.mi-wrap .pcv-over{color:#b3402f;font-weight:600}
.mi-wrap .pcv-under{color:#2f7a4f}
.mi-wrap .pcv-ok{color:var(--muted)}
.mi-wrap .pcv-nobasis{color:#8a7a3a;font-style:italic}
.mi-wrap .pcv-star{font-size:10px;color:#a5631b;margin-left:5px;white-space:nowrap;font-weight:700}
/* the lot-size caveat marker — the flag that separates artifact from signal on this tab */
.mi-wrap .pcv-lot{display:inline-block;margin-left:5px;padding:0 5px;border-radius:3px;background:#f1f0e6;color:#8a7a3a;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;cursor:help;vertical-align:1px}
/* Review-signal badges. A signal is something to act on, so it carries colour; a caveat explains a
   number away, so it stays muted like the `lot` badge it sits beside. Several can share one cell. */
.mi-wrap .pcv-sig{display:inline-block;margin:1px 4px 1px 0;padding:0 6px;border-radius:3px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;white-space:nowrap;cursor:help;vertical-align:1px}
.mi-wrap .pcv-sig.sig{background:#fdeee0;color:#a45a1e}
.mi-wrap .pcv-sig.cav{background:#f1f0e6;color:#8a7a3a}
.mi-wrap .pcv-chip{padding:1px 7px;border-radius:999px;font-size:11px;font-weight:700;white-space:nowrap}
.mi-wrap .pcv-chip.up{background:#fbe6e3;color:#b3402f}
.mi-wrap .pcv-chip.down{background:#e4f3ea;color:#2f7a4f}
.mi-wrap .pcv-chip.in{background:var(--panel2);color:var(--muted)}
.mi-wrap .pcv-chip.none{background:#f1f0e6;color:#8a7a3a}
.mi-wrap .pcv-kpi-click{cursor:pointer}
.mi-wrap .pcv-kpi-click:hover{border-color:var(--mi-accent)}
.mi-wrap .pcv-kpi-review{border-left-color:#c98a2b}
.mi-wrap .pcv-banner{max-width:900px;margin:0 auto 16px;padding:10px 14px;border:1px solid #e0c9a0;border-left:4px solid #c98a2b;border-radius:8px;background:#fdf6e8;color:#6a5a2a;font-size:12.5px;line-height:1.5;cursor:pointer}
.mi-wrap .pcv-banner:hover{border-color:var(--mi-accent)}
.mi-wrap .pcv-banner em{display:block;margin-top:4px;font-size:11.5px;color:#7a6a3a}
/* diverging mover meters — centre-anchored so increases run right and decreases left */
.mi-wrap .pcv-mrow{display:flex;align-items:center;gap:10px;padding:3px 0;cursor:pointer}
.mi-wrap .pcv-mrow:hover{background:var(--panel2)}
.mi-wrap .pcv-mpart{flex:0 0 150px;font-size:12px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mi-wrap .pcv-meter{position:relative;flex:1 1 auto;height:16px;background:var(--panel2);border-radius:4px;overflow:hidden}
.mi-wrap .pcv-tol{position:absolute;top:0;bottom:0;background:rgba(47,122,79,.10);border-left:1px dashed #a9c6b5;border-right:1px dashed #a9c6b5}
.mi-wrap .pcv-mid{position:absolute;left:50%;top:0;bottom:0;width:1px;background:var(--line)}
.mi-wrap .pcv-mbar{position:absolute;top:2px;bottom:2px;border-radius:3px}
.mi-wrap .pcv-mbar.up{background:#b3402f}
.mi-wrap .pcv-mbar.down{background:#2f7a4f}
.mi-wrap .pcv-mval{flex:0 0 320px;font-size:12px;text-align:right;font-variant-numeric:tabular-nums}
.mi-wrap .pcv-mdim{color:var(--muted)}
.mi-wrap .pcv-txn{margin-top:16px;border-top:1px solid var(--line);padding-top:10px}
@media (max-width:575px){.mi-wrap .pcv-mpart{flex-basis:110px}.mi-wrap .pcv-mval{flex-basis:170px}}
/* Colour is dropped or muddied by most office printers, so keep the band, meters and chips
   legible as shapes and outlines. The scatter still reads because circle-vs-triangle carries
   the in/out signal independently of colour. */
@media print{
  .mi-wrap .mi-filters,.mi-wrap .ship-filters{display:none !important}
  .mi-wrap .pcv-band{fill:#000 !important;fill-opacity:.06 !important}
  .mi-wrap .pcv-band-edge{stroke:#777 !important}
  .mi-wrap .pcv-band-lbl{fill:#444 !important}
  .mi-wrap .pcv-axlbl{fill:#444 !important}
  .mi-wrap .pcv-in{fill:#aaa !important}
  .mi-wrap .pcv-soft{fill:#fff !important}
  .mi-wrap .pcv-median{stroke:#888 !important}
  /* Colour is the only thing separating one line from another, so on a mono printer the lines are
     left in colour (they degrade to distinguishable greys) and the mute/hover state is dropped —
     a faded line on paper just looks like a printing fault. */
  .mi-wrap .pcv-line-mute,.mi-wrap .pcv-leg-mute{opacity:1 !important}
  .mi-wrap .pcv-lpt{stroke:#fff !important}
  .mi-wrap .pcv-line-lbl{font-size:8px}
  .mi-wrap .pcv-meter{background:#f4f4f4 !important;border:1px solid #ccc}
  .mi-wrap .pcv-tol{background:transparent !important}
  .mi-wrap .pcv-mid{background:#666 !important}
  .mi-wrap .pcv-chip,.mi-wrap .pcv-lot,.mi-wrap .pcv-sig{background:#fff !important;color:#111 !important;border:1px solid #999}
  .mi-wrap .pcv-nobasis,.mi-wrap .pcv-ok,.mi-wrap .pcv-star{color:#333 !important}
  .mi-wrap .pcv-banner{background:#fff !important;color:#111 !important}
}
html.dark-mode .mi-wrap{background:#1f242b;border-color:#3a414a;--bg:#232830;--panel:#232830;--panel2:#2b313a;--line:#3a414a;--text:#e8ebee;--muted:#aab3bd}
/* Part Cost Variance — only the semantic colours need dark overrides; everything expressed via
   MI's variables above is already handled by the variable swap on the line above. */
html.dark-mode .mi-wrap .pcv-band{fill-opacity:.13}
html.dark-mode .mi-wrap .pcv-band-edge{stroke:#4d7a63}
html.dark-mode .mi-wrap .pcv-band-lbl{fill:#8fb4a0}
html.dark-mode .mi-wrap .pcv-axlbl{fill:#8b95a1}
html.dark-mode .mi-wrap .pcv-in{fill:#7d8791}
/* Line colours are set inline from pcvLineColor(), so they cannot be swapped by a CSS rule.
   Lifting brightness/saturation as a group keeps the same twelve hues identifiable on the dark
   card without maintaining a second palette that could drift out of step with the first. */
html.dark-mode .mi-wrap .pcv-line{filter:brightness(1.45) saturate(1.15)}
html.dark-mode .mi-wrap .pcv-lpt{filter:brightness(1.45) saturate(1.15)}
html.dark-mode .mi-wrap .pcv-line-lbl{filter:brightness(1.6) saturate(1.1)}
html.dark-mode .mi-wrap .pcv-line-mute{opacity:.2}
html.dark-mode .mi-wrap .pcv-up-t{color:#e0a89e}
html.dark-mode .mi-wrap .pcv-dn-t{color:#8fd0a8}
html.dark-mode .mi-wrap .pcv-over{color:#e0a89e}
html.dark-mode .mi-wrap .pcv-under{color:#8fd0a8}
html.dark-mode .mi-wrap .pcv-nobasis,html.dark-mode .mi-wrap .pcv-star{color:#e0b566}
html.dark-mode .mi-wrap .pcv-lot{background:rgba(201,138,43,.18);color:#e0b566}
html.dark-mode .mi-wrap .pcv-sig.sig{background:rgba(164,90,30,.24);color:#e8b183}
html.dark-mode .mi-wrap .pcv-sig.cav{background:rgba(201,138,43,.16);color:#e0b566}
html.dark-mode .mi-wrap .pcv-chip.up{background:rgba(179,64,47,.20);color:#e0a89e}
html.dark-mode .mi-wrap .pcv-chip.down{background:rgba(47,122,79,.20);color:#8fd0a8}
html.dark-mode .mi-wrap .pcv-chip.none{background:rgba(201,138,43,.16);color:#e0b566}
html.dark-mode .mi-wrap .pcv-tol{background:rgba(47,122,79,.18);border-left-color:#4d7a63;border-right-color:#4d7a63}
html.dark-mode .mi-wrap .pcv-banner{background:rgba(201,138,43,.14);border-color:#7a5a2a;color:#e0b566}
html.dark-mode .mi-wrap .pcv-banner em{color:#cbb37a}
html.dark-mode .mi-wrap .pcv-mbar.up{background:#c04f3c}
html.dark-mode .mi-wrap .pcv-mbar.down{background:#3d8f5f}
html.dark-mode .mi-sub{color:#aab3bd}
html.dark-mode .mi-wrap .mi-pill{color:#cbd2d9}
html.dark-mode .mi-wrap .badge.live{background:rgba(47,122,79,.28)}
html.dark-mode .mi-wrap .badge.demo{background:rgba(201,138,43,.25);color:#e0b566}
html.dark-mode .mi-prov-pop{background:#232830;border-color:#3a414a;color:#e8ebee}
html.dark-mode .mi-prov-pop h3,html.dark-mode .mi-prov-pop .row b{color:#e8ebee}
html.dark-mode .mi-prov-pop .basis{color:#cbd2d9}
html.dark-mode .mi-prov-pop code,html.dark-mode .mi-prov-pop .sys{background:#2b313a;border-color:#3a414a;color:#cbd2d9}
</style>
